import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle, Tabs, Tab, Box, Badge, Autocomplete, TextField, List, ListItem, ListItemButton, ListItemText, Collapse, Chip } from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { toast } from 'react-toastify';
import { header } from './header.encry';

const RECENT_PLATFORM_SELECTIONS_KEY = 'pegasus.publishplatform.recentSelections.v1';
const RECENT_SELECTION_BUCKETS = ['stores', 'catalogs', 'categories', 'addons'];

const TabPanel = ({ children, value, index }) => (
    <div role="tabpanel" hidden={value !== index} style={{ paddingTop: '20px' }}>
        {value === index && <Box>{children}</Box>}
    </div>
);

const createEmptyRecentSelections = () => ({
    glovo: { stores: [], catalogs: [], categories: [], addons: [] },
    glovo_sisters: { stores: [], catalogs: [], categories: [], addons: [] },
});

const normalizeRecentSelections = (value) => {
    const base = createEmptyRecentSelections();
    const source = value && typeof value === 'object' ? value : {};

    ['glovo', 'glovo_sisters'].forEach((platformKey) => {
        const platformValue = source[platformKey];
        const normalizedPlatform = platformValue && typeof platformValue === 'object' ? platformValue : {};

        RECENT_SELECTION_BUCKETS.forEach((bucketKey) => {
            base[platformKey][bucketKey] = Array.isArray(normalizedPlatform[bucketKey])
                ? normalizedPlatform[bucketKey]
                : [];
        });
    });

    return base;
};

const readRecentPlatformSelections = () => {
    if (typeof window === 'undefined' || !window.localStorage) {
        return createEmptyRecentSelections();
    }

    try {
        const raw = window.localStorage.getItem(RECENT_PLATFORM_SELECTIONS_KEY);
        if (!raw) return createEmptyRecentSelections();
        return normalizeRecentSelections(JSON.parse(raw));
    } catch (error) {
        console.warn('Unable to read recent platform selections:', error);
        return createEmptyRecentSelections();
    }
};

const writeRecentPlatformSelections = (value) => {
    if (typeof window === 'undefined' || !window.localStorage) {
        return;
    }

    try {
        window.localStorage.setItem(
            RECENT_PLATFORM_SELECTIONS_KEY,
            JSON.stringify(normalizeRecentSelections(value))
        );
    } catch (error) {
        console.warn('Unable to write recent platform selections:', error);
    }
};

const upsertRecentSelectionEntries = (entries, items) => {
    const nextEntries = Array.isArray(entries) ? [...entries] : [];

    (Array.isArray(items) ? items : []).forEach((item) => {
        const value = item?.value;
        if (!value) return;

        const existingIndex = nextEntries.findIndex((entry) => `${entry?.value ?? ''}` === `${value}`);
        const previous = existingIndex >= 0 ? nextEntries[existingIndex] : null;
        const nextItem = {
            ...previous,
            ...item,
            count: Number(previous?.count || 0) + 1,
            lastUsedAt: Date.now(),
        };

        if (existingIndex >= 0) {
            nextEntries.splice(existingIndex, 1);
        }

        nextEntries.push(nextItem);
    });

    return nextEntries
        .sort((a, b) => {
            const countDiff = Number(b?.count || 0) - Number(a?.count || 0);
            if (countDiff !== 0) return countDiff;
            return Number(b?.lastUsedAt || 0) - Number(a?.lastUsedAt || 0);
        })
        .slice(0, 12);
};

const PublierPlateformesModal = ({
    open,
    onClose,
    product,
    // Aimer props
    aimerPlateforms,
    onSubmitAimer,
    aimerCategories = [],
    // Kooul props
    kooulPlateforms,
    onSubmitKooul,
    onSubmitMarjane,
    productCategories = [],
    stores = [],
    productAddons = [],
    glovoPlatform = null,
    plateforms = [],
    // Site props
    sitecategories,
    onSubmitSite,
    // Instagram props
    onSubmitInstagram,
    onSubmitDolibarr,
    onFetchDolibarrStock,
    onUpdateDolibarrStock,
    // Callback to refresh product data after successful operations
    onProductUpdated,
    allProducts = [],
    selectedProductIds = [],
    deliveryZoneOptions = [],
}) => {
    const [activeTab, setActiveTab] = useState(0);
    
    // All state is local - no syncing with parent until submit
    const [localAimerCategoryIds, setLocalAimerCategoryIds] = useState([]);
    const [localKooulPlateformId, setLocalKooulPlateformId] = useState(null);
    const [localKooulPlateform, setLocalKooulPlateform] = useState(null);
    const [, setLocalKooulCategoryIds] = useState([]);
    const [expandedKooulNodes, setExpandedKooulNodes] = useState(new Set());
    const [selectedKooulLeafCategory, setSelectedKooulLeafCategory] = useState(null);
    const [localMarjaneCategoryId, setLocalMarjaneCategoryId] = useState('');
    const [selectedMarjaneLeafCategory, setSelectedMarjaneLeafCategory] = useState(null);
    const [expandedMarjaneNodes, setExpandedMarjaneNodes] = useState(new Set());
    const [localSiteCategoryIds, setLocalSiteCategoryIds] = useState([]);
    const [localSiteFees, setLocalSiteFees] = useState(0);
    const [localSitePrice, setLocalSitePrice] = useState('');
    const [localSiteInStock, setLocalSiteInStock] = useState(true);
    const [localSiteVase, setLocalSiteVase] = useState('unspecified');
    const [localSiteExpress, setLocalSiteExpress] = useState(false);
    const [localSiteMorocco, setLocalSiteMorocco] = useState(false);
    const [localSiteExpressZones, setLocalSiteExpressZones] = useState([]);
    const initializedProductRef = useRef(null);
    const [localDolibarrCompanyId, setLocalDolibarrCompanyId] = useState('');
    const [dolibarrPriceDraft, setDolibarrPriceDraft] = useState('');
    const [dolibarrRefPrefix, setDolibarrRefPrefix] = useState('');
    const [dolibarrRefBase, setDolibarrRefBase] = useState('');
    const [dolibarrWarehouseId, setDolibarrWarehouseId] = useState('');
    const [dolibarrTargetStock, setDolibarrTargetStock] = useState('1');
    const [dolibarrStockState, setDolibarrStockState] = useState(null);
    const [isDolibarrStockLoading, setIsDolibarrStockLoading] = useState(false);
    const [isDolibarrStockSubmitting, setIsDolibarrStockSubmitting] = useState(false);
    const [selectedGlovoCatalogId, setSelectedGlovoCatalogId] = useState(null);
    const [selectedGlovoCategoryId, setSelectedGlovoCategoryId] = useState(null);
    const [selectedGlovoAddonIds, setSelectedGlovoAddonIds] = useState([]);
    const [glovoPriceDraft, setGlovoPriceDraft] = useState('');
    const [glovoDescriptionDraft, setGlovoDescriptionDraft] = useState('');
    const [selectedGlovoSistersCatalogId, setSelectedGlovoSistersCatalogId] = useState(null);
    const [selectedGlovoSistersCategoryId, setSelectedGlovoSistersCategoryId] = useState(null);
    const [selectedGlovoSistersAddonIds, setSelectedGlovoSistersAddonIds] = useState([]);
    const [glovoSistersPriceDraft, setGlovoSistersPriceDraft] = useState('');
    const [glovoSistersDescriptionDraft, setGlovoSistersDescriptionDraft] = useState('');
    const [instagramDescriptionDraft, setInstagramDescriptionDraft] = useState('');
    const [isGlovoSubmitting, setIsGlovoSubmitting] = useState(false);
    const [isGlovoSistersSubmitting, setIsGlovoSistersSubmitting] = useState(false);
    const [isBulkGlovoSubmitting, setIsBulkGlovoSubmitting] = useState(false);
    const [isBulkGlovoSistersSubmitting, setIsBulkGlovoSistersSubmitting] = useState(false);
    const [isAimerSubmitting, setIsAimerSubmitting] = useState(false);
    const [isKooulSubmitting, setIsKooulSubmitting] = useState(false);
    const [isMarjaneSubmitting, setIsMarjaneSubmitting] = useState(false);
    const [isSiteSubmitting, setIsSiteSubmitting] = useState(false);
    const [isInstagramSubmitting, setIsInstagramSubmitting] = useState(false);
    const [isDolibarrSubmitting, setIsDolibarrSubmitting] = useState(false);
    const [deletingPlateformIds, setDeletingPlateformIds] = useState(new Set());
    const [recentPlatformSelections, setRecentPlatformSelections] = useState(() => readRecentPlatformSelections());
    const [bulkGlovoQueue, setBulkGlovoQueue] = useState([]);
    const [bulkGlovoSistersQueue, setBulkGlovoSistersQueue] = useState([]);

    const textareaRef = useRef(null);

    const sanitizeDolibarrRefPart = (value) => String(value || '')
        .trim()
        .replace(/[^a-zA-Z0-9._-]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 64);

    const buildDolibarrRefBase = (targetProduct) => {
        const sourceParts = [
            targetProduct?.reference,
            targetProduct?.ref,
            targetProduct?.sku,
            targetProduct?.barcode,
            targetProduct?._id,
        ];
        const sourceBase = sourceParts
            .map((part) => sanitizeDolibarrRefPart(part))
            .find(Boolean) || 'PEG';
        const uniqueTail = sanitizeDolibarrRefPart(`${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`).slice(0, 12);
        return sanitizeDolibarrRefPart(`${sourceBase}-${uniqueTail}`).slice(0, 64);
    };

    // Always return a stable string ID; handle nested ObjectId-like shapes
    const normalizeId = (value, seen = new Set()) => {
        if (value === null || value === undefined) return '';
        if (typeof value !== 'object') return String(value);
        if (seen.has(value)) return '';

        seen.add(value);

        if (value.$oid) return String(value.$oid);
        if (value._id?.$oid) return String(value._id.$oid);

        const candidates = [value._id, value.id, value.value, value.plateform_id];
        for (const candidate of candidates) {
            if (candidate !== undefined && candidate !== value) {
                const normalized = normalizeId(candidate, seen);
                if (normalized) {
                    return normalized;
                }
            }
        }

        return '';
    };

    // Deduplicate options by normalized value
    const uniqueByValue = (options) => {
        const seen = new Set();
        return options.filter((opt) => {
            const key = normalizeId(opt?.value);
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    };

    // Initialize state when modal opens
    useEffect(() => {
        if (!open) {
            initializedProductRef.current = null;
            return;
        }
        if (product && initializedProductRef.current === product._id) return;
        console.log('[PublierPlateformesModal] useEffect triggered', { open, product: product?._id });
        if (open && product) {
            initializedProductRef.current = product._id;
            console.log('[PublierPlateformesModal] Initializing modal state', {
                productId: product._id,
                productName: product.name,
                description: product?.description
            });
            setActiveTab(0);
            
            // Reset all state to product defaults
            setLocalAimerCategoryIds([]);
            setLocalKooulPlateformId(null);
            setLocalKooulPlateform(null);
            setLocalKooulCategoryIds([]);
            setExpandedKooulNodes(new Set());
            setSelectedKooulLeafCategory(null);
            setLocalMarjaneCategoryId('');
            setSelectedMarjaneLeafCategory(null);
            setExpandedMarjaneNodes(new Set());
            setLocalSiteCategoryIds([]);
            setLocalSiteFees(0);
            setLocalSitePrice(product?.regular_price !== undefined && product?.regular_price !== null ? String(product.regular_price) : '');
            setLocalSiteInStock(true);
            setLocalSiteVase(product?.vase_inclusion || 'unspecified');
            setLocalSiteExpress(product?.website_delivery?.express_enabled === true);
            setLocalSiteMorocco(product?.website_delivery?.morocco_enabled === true);
            setLocalSiteExpressZones(Array.isArray(product?.website_delivery?.express_zones) ? product.website_delivery.express_zones : []);
            setLocalDolibarrCompanyId(product?.dolibarr_company_id || '');
            setDolibarrPriceDraft(product?.regular_price !== undefined && product?.regular_price !== null ? String(product.regular_price) : '');
            setDolibarrRefPrefix('');
            setDolibarrRefBase(buildDolibarrRefBase(product));
            setDolibarrWarehouseId('');
            setDolibarrTargetStock('1');
            setDolibarrStockState(null);
            setSelectedGlovoCatalogId(null);
            setSelectedGlovoCategoryId(null);
            setSelectedGlovoSistersCatalogId(null);
            setSelectedGlovoSistersCategoryId(null);
            setSelectedGlovoAddonIds([]);
            setSelectedGlovoSistersAddonIds([]);
            const initialDesc = product?.description || '';
            const initialPrice = product?.regular_price ?? '';
            console.log('[PublierPlateformesModal] Setting initial description', { initialDesc });
            setGlovoPriceDraft(initialPrice === '' || initialPrice === null || initialPrice === undefined ? '' : String(initialPrice));
            setGlovoSistersPriceDraft(initialPrice === '' || initialPrice === null || initialPrice === undefined ? '' : String(initialPrice));
            setGlovoDescriptionDraft(initialDesc);
            setGlovoSistersDescriptionDraft(initialDesc);
            setInstagramDescriptionDraft(initialDesc);
            setDeletingPlateformIds(new Set());
            setBulkGlovoQueue([]);
        }
    }, [open, product]);

    // Close/hide Bootstrap modals and disable their focus enforcement
    useEffect(() => {
        if (!open) return;

        console.log('[PublierPlateformesModal] Handling Bootstrap modal conflicts');

        // Find all open Bootstrap modals
        const bootstrapModals = Array.from(document.querySelectorAll('.modal.show'));
        const backdrops = Array.from(document.querySelectorAll('.modal-backdrop'));
        const savedStates = [];

        bootstrapModals.forEach((modalEl) => {
            if (modalEl instanceof HTMLElement) {
                console.log('[PublierPlateformesModal] Found open Bootstrap modal:', modalEl.id || modalEl.className);
                
                // Save current state
                const currentState = {
                    element: modalEl,
                    display: modalEl.style.display,
                    visibility: modalEl.style.visibility,
                    hadShow: modalEl.classList.contains('show'),
                    focus: modalEl.getAttribute('data-bs-focus'),
                    ariaHidden: modalEl.getAttribute('aria-hidden')
                };
                savedStates.push(currentState);

                // Disable Bootstrap's focus enforcement
                modalEl.setAttribute('data-bs-focus', 'false');
                
                // Hide the modal visually but keep it in DOM with all event listeners
                // Use visibility instead of display to preserve layout and listeners
                modalEl.style.visibility = 'hidden';
                modalEl.style.pointerEvents = 'none';
                modalEl.setAttribute('aria-hidden', 'true');
                
                console.log('[PublierPlateformesModal] Hidden Bootstrap modal (preserved event listeners)');
            }
        });

        // Hide backdrops
        const backdropStates = [];
        backdrops.forEach((el) => {
            if (el instanceof HTMLElement) {
                backdropStates.push({
                    element: el,
                    display: el.style.display,
                    visibility: el.style.visibility
                });
                el.style.visibility = 'hidden';
                el.style.pointerEvents = 'none';
            }
        });

        // Remove modal-open class from body
        const hadModalOpen = document.body.classList.contains('modal-open');
        const originalOverflow = document.body.style.overflow;
        const originalPaddingRight = document.body.style.paddingRight;
        
        if (hadModalOpen) {
            document.body.classList.remove('modal-open');
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
        }

        console.log('[PublierPlateformesModal] Bootstrap modals suspended, MUI Dialog is in control');

        // Cleanup: restore Bootstrap modals when MUI Dialog closes
        return () => {
            console.log('[PublierPlateformesModal] Restoring Bootstrap modals');

            savedStates.forEach(({ element, display, visibility, hadShow, focus, ariaHidden }) => {
                // Restore visibility and pointer events
                element.style.visibility = visibility || '';
                element.style.pointerEvents = '';
                element.style.display = display || '';
                
                // Restore aria-hidden
                if (ariaHidden !== null) {
                    element.setAttribute('aria-hidden', ariaHidden);
                } else {
                    element.removeAttribute('aria-hidden');
                }
                
                // Restore focus attribute
                if (focus !== null && focus !== 'false') {
                    element.setAttribute('data-bs-focus', focus);
                } else {
                    element.removeAttribute('data-bs-focus');
                }
                
                console.log('[PublierPlateformesModal] Restored Bootstrap modal:', element.id || element.className);
            });

            // Restore backdrops
            backdropStates.forEach(({ element, display, visibility }) => {
                element.style.display = display || '';
                element.style.visibility = visibility || '';
                element.style.pointerEvents = '';
            });

            // Restore body classes if needed
            if (hadModalOpen) {
                document.body.classList.add('modal-open');
                document.body.style.overflow = originalOverflow;
                document.body.style.paddingRight = originalPaddingRight;
            }

            console.log('[PublierPlateformesModal] Bootstrap modals restored');
        };
    }, [open]);

    // Prevent Bootstrap's focusin event handler from stealing focus
    useEffect(() => {
        if (!open) return;

        const preventBootstrapFocusTrap = (e) => {
            // Check if the focus target is inside the MUI Dialog
            const muiDialog = document.querySelector('[role="dialog"][aria-modal="true"]');
            if (muiDialog && muiDialog.contains(e.target)) {
                // Stop Bootstrap from interfering
                e.stopImmediatePropagation();
                console.log('[PublierPlateformesModal] Blocked Bootstrap focus trap on:', e.target);
            }
        };

        // Capture phase to intercept before Bootstrap's listener
        document.addEventListener('focusin', preventBootstrapFocusTrap, true);
        console.log('[PublierPlateformesModal] Added focusin prevention listener');

        return () => {
            document.removeEventListener('focusin', preventBootstrapFocusTrap, true);
            console.log('[PublierPlateformesModal] Removed focusin prevention listener');
        };
    }, [open]);

    // Shared props to avoid overlays blocking UI and keep menus above dialog
    // No shared menu props to avoid interference; use react-select defaults

    // Memoize Aimer category options
    const aimerCategoryOptions = useMemo(() => {
        if (!Array.isArray(aimerCategories)) {
            return [];
        }

        const mapped = aimerCategories.map((cat) => ({
            value: normalizeId(cat._id || cat.id || cat.value),
            label: cat.name || cat.label || 'Catégorie',
            remote_plateform_id: cat.remote_plateform_id,
            link_ids: cat.link_ids,
        }));

        return uniqueByValue(mapped);
    }, [aimerCategories]);

    // Memoize Kooul platform options
    const kooulPlateformOptions = useMemo(() => {
        if (!Array.isArray(kooulPlateforms)) {
            return [];
        }

        const mapped = kooulPlateforms.map((plateform) => {
            const optionValue = normalizeId(plateform._id || plateform.id || plateform.plateform_id);
            const storeEntries = Array.isArray(plateform?.store_ids) ? plateform.store_ids : [];
            const storeLabels = storeEntries
                .map((entry) => {
                    const storeId = normalizeId(entry?.store_id || entry);
                    const matchingStore = stores.find((store) =>
                        store.value === storeId || store.rawId === storeId || store.storeCode === storeId
                    );
                    return matchingStore?.label || null;
                })
                .filter(Boolean);
            
            const storeSuffix = storeLabels.length > 0 ? ` (${storeLabels.join(', ')})` : '';
            
            // Spread first so our computed value/label are not overwritten
            return {
                ...plateform,
                value: optionValue,
                label: (plateform?.name || optionValue || 'Plateforme Kooul') + storeSuffix,
                categories: plateform.categories,
            };
        });

        return uniqueByValue(mapped);
    }, [kooulPlateforms, stores]);

    // Memoize Site category options (assuming sitecategories already formatted)
    const siteCategoryOptions = useMemo(() => {
        if (!Array.isArray(sitecategories)) {
            return [];
        }
        const mapped = sitecategories.map(cat => ({
            ...cat,
            value: normalizeId(cat.value || cat._id || cat.id)
        }));

        return uniqueByValue(mapped);
    }, [sitecategories]);

    const dolibarrCompanyOptions = useMemo(() => {
        const dolibarrPlateforms = (Array.isArray(plateforms) ? plateforms : []).filter(
            (plateform) => `${plateform?.type || ''}`.toLowerCase() === 'dolibarr'
        );
        const options = [];

        dolibarrPlateforms.forEach((plateform) => {
            const additionnalData = plateform?.additionnal_data;
            if (!additionnalData || typeof additionnalData !== 'object' || Array.isArray(additionnalData)) {
                return;
            }

            Object.entries(additionnalData).forEach(([companyId, companyConfig]) => {
                if (!companyId || !companyConfig || typeof companyConfig !== 'object') {
                    return;
                }

                options.push({
                    value: companyId,
                    label: companyConfig.name || companyId,
                    plateformId: normalizeId(plateform?._id || plateform?.id),
                    url: companyConfig.url || '',
                });
            });
        });

        return uniqueByValue(options);
    }, [plateforms]);
    const dolibarrLinkedEntries = useMemo(() => {
        const dolibarrPlateformIds = new Set(
            dolibarrCompanyOptions
                .map((option) => normalizeId(option?.plateformId))
                .filter(Boolean)
        );
        const productPlateforms = Array.isArray(product?.plateforms) ? product.plateforms : [];
        const parsedEntries = productPlateforms
            .filter((entry) => dolibarrPlateformIds.has(normalizeId(entry?.plateform_id)))
            .map((entry) => {
                const roadmapEntries = Array.isArray(entry?.roadmap) ? entry.roadmap : [];
                const roadmapEntry = roadmapEntries.find((item) => {
                    const type = `${item?.type || ''}`.toLowerCase();
                    return type === 'dolibarr' || item?.data?.company_id || item?.data?.ref || item?.data?.dolibarr_id;
                }) || null;
                const companyId = normalizeId(roadmapEntry?.data?.company_id);
                const companyOption = dolibarrCompanyOptions.find((option) => option.value === companyId) || null;
                const remoteIds = Array.isArray(entry?.remote_id) ? entry.remote_id : [];
                const remoteIdValue = roadmapEntry?.remote_id || remoteIds[0] || '';
                const parsedDolibarrId = Number(roadmapEntry?.data?.dolibarr_id ?? remoteIdValue);

                return {
                    plateformId: normalizeId(entry?.plateform_id),
                    companyId,
                    companyName: roadmapEntry?.data?.company_name || companyOption?.label || '',
                    dolibarrId: Number.isFinite(parsedDolibarrId) && parsedDolibarrId > 0 ? parsedDolibarrId : null,
                    dolibarrRef: `${roadmapEntry?.data?.ref || ''}`.trim(),
                };
            })
            .filter((entry) => entry.companyId || entry.dolibarrId || entry.dolibarrRef);

        if (parsedEntries.length > 0) {
            return parsedEntries;
        }

        const fallbackCompanyId = normalizeId(product?.dolibarr_company_id);
        const fallbackPlateform = dolibarrCompanyOptions.find((option) => option.value === fallbackCompanyId) || dolibarrCompanyOptions[0] || null;
        const fallbackId = Number(product?.dolibarr_id);
        const fallbackRef = `${product?.dolibarr_ref || ''}`.trim();

        if (!fallbackPlateform || (!fallbackRef && !(Number.isFinite(fallbackId) && fallbackId > 0))) {
            return [];
        }

        return [{
            plateformId: normalizeId(fallbackPlateform?.plateformId),
            companyId: fallbackCompanyId || normalizeId(fallbackPlateform?.value),
            companyName: product?.dolibarr_company_name || fallbackPlateform?.label || '',
            dolibarrId: Number.isFinite(fallbackId) && fallbackId > 0 ? fallbackId : null,
            dolibarrRef: fallbackRef,
        }];
    }, [dolibarrCompanyOptions, product?.plateforms, product?.dolibarr_company_id, product?.dolibarr_company_name, product?.dolibarr_id, product?.dolibarr_ref]);

    // Derive selected Kooul platform value first (needed for category options)
    const selectedKooulPlateformValue = useMemo(() => {
        return kooulPlateformOptions.find(opt => opt.value === localKooulPlateformId) || null;
    }, [kooulPlateformOptions, localKooulPlateformId]);

    // Build Kooul category tree structure
    const kooulCategoryTree = useMemo(() => {
        if (!localKooulPlateformId || !Array.isArray(productCategories)) {
            return [];
        }

        const filtered = productCategories.filter((cat) => {
            if (cat.type !== 'kooul') return false;
            return normalizeId(cat.plateform_id) === localKooulPlateformId;
        });

        // Build a map of categories by ID
        const categoryMap = new Map();
        filtered.forEach((cat) => {
            const id = normalizeId(cat._id?.$oid || cat._id || cat.id);
            categoryMap.set(id, {
                id,
                name: cat.name || 'Catégorie',
                parent_id: cat.parent_id ? normalizeId(cat.parent_id?.$oid || cat.parent_id) : null,
                remote_plateform_id: cat.remote_plateform_id,
                children: []
            });
        });

        // Build the tree structure
        const roots = [];
        categoryMap.forEach((category) => {
            if (category.parent_id) {
                const parent = categoryMap.get(category.parent_id);
                if (parent) {
                    parent.children.push(category);
                } else {
                    // Parent not found, treat as root
                    roots.push(category);
                }
            } else {
                roots.push(category);
            }
        });

        return roots;
    }, [localKooulPlateformId, productCategories]);

    const marjaneCategoryTree = useMemo(() => {
        if (!Array.isArray(productCategories)) {
            return [];
        }

        const marjaneOnly = productCategories.filter((cat) => `${cat?.type ?? ''}`.toLowerCase() === 'marjanemall');
        const categoryMap = new Map();

        marjaneOnly.forEach((cat) => {
            const id = normalizeId(cat?._id?.$oid || cat?._id || cat?.id || cat?.value);
            if (!id) return;

            const parentId = normalizeId(cat?.parent_id?.$oid || cat?.parent_id || cat?.parentId || '');
            const remoteId = normalizeId(
                cat?.remote_id ||
                cat?.remoteId ||
                cat?.remote_plateform_id ||
                cat?.remote_category_id ||
                cat?.category_id ||
                ''
            );

            categoryMap.set(id, {
                id,
                name: cat?.name || cat?.label || 'Catégorie Marjane',
                parentId: parentId || '',
                remoteId,
                raw: cat,
                children: []
            });
        });

        const roots = [];
        categoryMap.forEach((node) => {
            if (node.parentId && categoryMap.has(node.parentId)) {
                categoryMap.get(node.parentId).children.push(node);
            } else {
                roots.push(node);
            }
        });

        return roots;
    }, [productCategories]);

    const marjaneLeafOptions = useMemo(() => {
        const options = [];

        const traverse = (nodes, path = []) => {
            (nodes || []).forEach((node) => {
                const nextPath = [...path, node.name];
                if (!node.children || node.children.length === 0) {
                    options.push({
                        value: node.id,
                        label: nextPath.join(' > '),
                        categoryName: node.name,
                        remoteId: node.remoteId,
                        node
                    });
                    return;
                }
                traverse(node.children, nextPath);
            });
        };

        traverse(marjaneCategoryTree, []);
        return options;
    }, [marjaneCategoryTree]);

    const selectedMarjaneSearchOption = useMemo(
        () => marjaneLeafOptions.find((opt) => opt.value === localMarjaneCategoryId) || null,
        [marjaneLeafOptions, localMarjaneCategoryId]
    );

    // Helper: Check if a category is a leaf (has no children)
    const isLeafCategory = (category) => {
        return !category.children || category.children.length === 0;
    };

    // Helper: Get full path from root to a category
    const getCategoryPath = (categoryId) => {
        if (!localKooulPlateformId || !Array.isArray(productCategories)) {
            return [];
        }

        const filtered = productCategories.filter((cat) => {
            if (cat.type !== 'kooul') return false;
            return normalizeId(cat.plateform_id) === localKooulPlateformId;
        });

        const categoryMap = new Map();
        filtered.forEach((cat) => {
            const id = normalizeId(cat._id?.$oid || cat._id || cat.id);
            categoryMap.set(id, {
                id,
                name: cat.name || 'Catégorie',
                parent_id: cat.parent_id ? normalizeId(cat.parent_id?.$oid || cat.parent_id) : null,
            });
        });

        const path = [];
        let current = categoryMap.get(categoryId);
        while (current) {
            path.unshift(current);
            current = current.parent_id ? categoryMap.get(current.parent_id) : null;
        }
        return path;
    };

    // Toggle expand/collapse for tree nodes
    const toggleKooulNode = (nodeId) => {
        setExpandedKooulNodes(prev => {
            const newSet = new Set(prev);
            if (newSet.has(nodeId)) {
                newSet.delete(nodeId);
            } else {
                newSet.add(nodeId);
            }
            return newSet;
        });
    };

    // Select a leaf category
    const selectKooulLeafCategory = (category) => {
        if (isLeafCategory(category)) {
            setSelectedKooulLeafCategory(category);
            setLocalKooulCategoryIds([category.id]);
        }
    };

    const toggleMarjaneNode = (nodeId) => {
        setExpandedMarjaneNodes((prev) => {
            const next = new Set(prev);
            if (next.has(nodeId)) next.delete(nodeId);
            else next.add(nodeId);
            return next;
        });
    };

    const selectMarjaneLeafCategory = (category) => {
        if (!category || !isLeafCategory(category)) return;
        setSelectedMarjaneLeafCategory(category);
        setLocalMarjaneCategoryId(category.id);
    };

    const renderMarjaneCategoryTree = (categories, level = 0) => {
        if (!Array.isArray(categories) || categories.length === 0) {
            return null;
        }

        return categories.map((category) => {
            const isExpanded = expandedMarjaneNodes.has(category.id);
            const isLeaf = isLeafCategory(category);
            const isSelected = selectedMarjaneLeafCategory?.id === category.id;
            const hasChildren = Array.isArray(category.children) && category.children.length > 0;

            return (
                <React.Fragment key={category.id}>
                    <ListItem disablePadding sx={{ pl: level * 3 }}>
                        <ListItemButton
                            onClick={() => (isLeaf ? selectMarjaneLeafCategory(category) : toggleMarjaneNode(category.id))}
                            selected={isSelected}
                            sx={{
                                bgcolor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                                '&:hover': { bgcolor: isSelected ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)' },
                                borderLeft: isSelected ? '3px solid #1976d2' : 'none',
                            }}
                        >
                            {hasChildren ? (
                                <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                </Box>
                            ) : (
                                <Box sx={{ mr: 1, ml: 3, display: 'flex', alignItems: 'center' }}>
                                    <ChevronRightIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                </Box>
                            )}
                            <ListItemText
                                primary={category.name}
                                secondary={isLeaf ? `remote_id: ${category.remoteId || 'non défini'}` : null}
                            />
                        </ListItemButton>
                    </ListItem>

                    {hasChildren && (
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding dense>
                                {renderMarjaneCategoryTree(category.children, level + 1)}
                            </List>
                        </Collapse>
                    )}
                </React.Fragment>
            );
        });
    };

    // Auto-select Kooul platform if categories already exist
    useEffect(() => {
        if (!open || !Array.isArray(productCategories) || !Array.isArray(kooulPlateformOptions)) {
            return;
        }

        // Find existing kooul categories
        const kooulCategories = productCategories.filter(cat => cat.type === 'kooul');
        
        if (kooulCategories.length > 0 && !localKooulPlateformId) {
            // Get the plateform_id from the first kooul category
            const firstCategory = kooulCategories[0];
            const plateformId = normalizeId(firstCategory.plateform_id);
            
            if (plateformId) {
                console.log('[PublierPlateformesModal] Auto-selecting Kooul platform from existing categories', { plateformId });
                
                // Find the matching platform option
                const matchingPlatform = kooulPlateformOptions.find(opt => 
                    normalizeId(opt.value) === plateformId
                );
                
                if (matchingPlatform) {
                    setLocalKooulPlateformId(plateformId);
                    setLocalKooulPlateform(matchingPlatform);
                    console.log('[PublierPlateformesModal] Kooul platform auto-selected', { matchingPlatform });
                }
            }
        }
    }, [open, productCategories, kooulPlateformOptions, localKooulPlateformId]);

    useEffect(() => {
        if (!open) return;
        if (localDolibarrCompanyId) return;
        if (dolibarrLinkedEntries.length > 0) {
            setLocalDolibarrCompanyId(dolibarrLinkedEntries[0].companyId);
            return;
        }
        if (dolibarrCompanyOptions.length === 1) {
            setLocalDolibarrCompanyId(dolibarrCompanyOptions[0].value);
        }
    }, [open, dolibarrCompanyOptions, dolibarrLinkedEntries, localDolibarrCompanyId]);

    useEffect(() => {
        const selectedLinkedEntry = dolibarrLinkedEntries.find((entry) => entry.companyId === localDolibarrCompanyId) || null;
        if (!open || activeTab !== 7 || !selectedLinkedEntry) return;
        if (!localDolibarrCompanyId || typeof onFetchDolibarrStock !== 'function') return;

        let cancelled = false;
        const loadDolibarrStock = async () => {
            setIsDolibarrStockLoading(true);
            try {
                const payload = await onFetchDolibarrStock(localDolibarrCompanyId);
                if (cancelled) return;
                setDolibarrStockState(payload);
                const stockEntries = Array.isArray(payload?.stock) ? payload.stock : [];
                const warehouses = Array.isArray(payload?.warehouses) ? payload.warehouses : [];
                if (stockEntries.length > 0) {
                    const firstEntry = stockEntries[0];
                    const nextWarehouseId = String(firstEntry?.warehouse_id || firstEntry?.id || firstEntry?.fk_entrepot || '');
                    const nextStock = Number(firstEntry?.stock_reel ?? firstEntry?.real ?? firstEntry?.qty ?? firstEntry?.stock ?? 0);
                    if (nextWarehouseId) {
                        setDolibarrWarehouseId(nextWarehouseId);
                    }
                    if (Number.isFinite(nextStock)) {
                        setDolibarrTargetStock(String(nextStock));
                    }
                } else if (warehouses.length > 0) {
                    const firstWarehouseId = String(warehouses[0]?.id || warehouses[0]?.rowid || '');
                    if (firstWarehouseId) {
                        setDolibarrWarehouseId(firstWarehouseId);
                    }
                    setDolibarrTargetStock('0');
                }
            } catch (error) {
                if (cancelled) return;
                console.error('Failed to fetch Dolibarr stock:', error);
                setDolibarrStockState(null);
            } finally {
                if (!cancelled) {
                    setIsDolibarrStockLoading(false);
                }
            }
        };

        loadDolibarrStock();
        return () => {
            cancelled = true;
        };
    }, [open, activeTab, dolibarrLinkedEntries, localDolibarrCompanyId, onFetchDolibarrStock]);

    // Auto-expand tree and select leaf category if Kooul platform is set
    useEffect(() => {
        if (!localKooulPlateformId || kooulCategoryTree.length === 0 || selectedKooulLeafCategory) {
            return;
        }

        // Find all leaf categories in the tree
        const findLeafCategories = (categories) => {
            const leaves = [];
            categories.forEach(cat => {
                if (isLeafCategory(cat)) {
                    leaves.push(cat);
                } else if (cat.children && cat.children.length > 0) {
                    leaves.push(...findLeafCategories(cat.children));
                }
            });
            return leaves;
        };

        const leafCategories = findLeafCategories(kooulCategoryTree);
        
        // If there's only one leaf category, auto-select it and expand the path to it
        if (leafCategories.length === 1) {
            const leaf = leafCategories[0];
            console.log('[PublierPlateformesModal] Auto-selecting single leaf category', { leaf });
            
            // Build the path to this leaf and expand all parent nodes
            const path = getCategoryPath(leaf.id);
            const nodesToExpand = new Set();
            path.forEach(cat => {
                if (!isLeafCategory(cat)) {
                    nodesToExpand.add(cat.id);
                }
            });
            
            setExpandedKooulNodes(nodesToExpand);
            setSelectedKooulLeafCategory(leaf);
            setLocalKooulCategoryIds([leaf.id]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localKooulPlateformId, kooulCategoryTree, selectedKooulLeafCategory]);

    // Derive all selected values from IDs by filtering memoized options
    const selectedAimerCategoriesValue = useMemo(() => 
        aimerCategoryOptions.filter(opt => localAimerCategoryIds.includes(opt.value)),
        [aimerCategoryOptions, localAimerCategoryIds]
    );
    
    const selectedSiteCategoriesValue = useMemo(() => 
        siteCategoryOptions.filter(opt => localSiteCategoryIds.includes(opt.value)),
        [siteCategoryOptions, localSiteCategoryIds]
    );
    
    // Glovo catalog options (distinct catalog_id from productCategories type="glovo")
    const glovoCatalogOptions = useMemo(() => {
        if (!Array.isArray(productCategories)) {
            return [];
        }
        
        // Get all catalogIds that already exist in the product's roadmap
        const existingCatalogIds = new Set();
        if (product?.plateforms && Array.isArray(product.plateforms)) {
            product.plateforms.forEach(platform => {
                if (Array.isArray(platform.roadmap)) {
                    platform.roadmap.forEach(roadmapEntry => {
                        if (roadmapEntry.type === 'glovo' && roadmapEntry.data?.catalogIds) {
                            roadmapEntry.data.catalogIds.forEach(catId => {
                                existingCatalogIds.add(String(catId));
                            });
                        }
                    });
                }
            });
        }
        
        const glovoCategories = productCategories.filter(cat => cat.type === 'glovo');
        const catalogMap = new Map();
        
        // Get first record for each catalog_id
        glovoCategories.forEach(cat => {
            const catalogId = cat.catalog_id || cat.catalogId || cat._catalogId;
            if (catalogId && !catalogMap.has(String(catalogId))) {
                catalogMap.set(String(catalogId), cat);
            }
        });
        
        // Create options with store names from the first record, excluding existing catalogs
        return Array.from(catalogMap.entries())
            .filter(([catalogId]) => !existingCatalogIds.has(String(catalogId)))
            .map(([catalogId, firstRecord]) => {
                // Handle store_id which might be an object with $oid
                let storeId = firstRecord.store_id;
                if (storeId?.$oid) {
                    storeId = storeId.$oid;
                }
                storeId = normalizeId(storeId);
                
                const store = stores.find(s => {
                    const sId = normalizeId(s._id || s.id);
                    const sValue = normalizeId(s.value);
                    const sRawId = normalizeId(s.rawId);
                    return sId === storeId || sValue === storeId || sRawId === storeId;
                });
                
                const storeName = store ? (store.name || store.label) : null;
                const storeCode = store ? store.storeCode : null;
                
                return {
                    value: catalogId,
                    label: storeName ? `Catalog ${catalogId} - ${storeName}` : `Catalog ${catalogId}`,
                    storeName: storeName,
                    storeId: storeId,
                    storeCode: storeCode
                };
            });
    }, [productCategories, stores, product]);
    
    // Check if description is compliant
    const isDescriptionCompliant = (desc) => {
        if (!desc || desc.trim().length === 0) return false;
        const charCount = desc.length;
        return charCount >= 50 && charCount <= 200;
    };
    
    // Glovo addon options (filter addons with portal-enabled platforms)
    const glovoAddonOptions = useMemo(() => {
        if (!Array.isArray(productAddons) || !glovoPlatform) {
            return [];
        }
        
        const glovoPlatformId = normalizeId(glovoPlatform._id || glovoPlatform.id);
        
        // Filter addons that have plateforms_keys matching the Glovo portal platform
        const portalAddons = productAddons.filter(addon => {
            if (!Array.isArray(addon.plateforms_keys)) return false;
            
            return addon.plateforms_keys.some(pk => {
                const platformId = normalizeId(pk.plateform_id || pk.plateform_id?.$oid);
                return platformId === glovoPlatformId && pk.remote_id;
            });
        });
        
        return portalAddons.map(addon => {
            const addonId = normalizeId(addon._id || addon._id?.$oid);
            
            // Find the plateform_key matching the Glovo portal platform to get remote_id
            const portalKey = addon.plateforms_keys?.find(pk => {
                const platformId = normalizeId(pk.plateform_id || pk.plateform_id?.$oid);
                return platformId === glovoPlatformId && pk.remote_id;
            });
            const remoteId = portalKey?.remote_id || '';
            
            // Get first 5 options
            const options = Array.isArray(addon.options) ? addon.options.slice(0, 5) : [];
            const optionNames = options.map(opt => opt.name).filter(Boolean);
            
            return {
                value: addonId,
                label: addon.name || 'Addon',
                remoteId: remoteId,
                optionNames: optionNames,
                fullAddon: addon
            };
        });
    }, [productAddons, glovoPlatform]);

    // Derive Glovo Sisters platform from product plateforms (if any)
    const normalizePlatformKey = (s) => {
        if (!s) return '';
        return String(s).toLowerCase().replace(/[^a-z0-9]+/g, '_');
    };

    const extractPlatformTypeName = (p) => {
        if (!p) return { typeVal: '', nameVal: '' };
        const typeVal = p.type || p.additionnal_data?.type || p.additional_data?.type || '';
        const nameVal = p.name || p.additionnal_data?.name || p.additional_data?.name || '';
        return { typeVal, nameVal };
    };

    // Aggregate all known platforms from multiple sources and deduplicate by id
    const aggregatedPlatforms = useMemo(() => {
        const sources = [];
        if (Array.isArray(product?.plateforms)) sources.push(...product.plateforms);
        if (Array.isArray(kooulPlateforms)) sources.push(...kooulPlateforms);
        if (Array.isArray(aimerPlateforms)) sources.push(...aimerPlateforms);
        if (glovoPlatform) sources.push(glovoPlatform);
        if (Array.isArray(plateforms)) sources.push(...plateforms);

        const seen = new Set();
        const result = [];
        for (const p of sources) {
            const id = normalizeId(p?._id || p?.id || p?.plateform_id || p?.value);
            if (!id) continue;
            if (seen.has(id)) continue;
            seen.add(id);
            result.push(p);
        }
        return result;
    }, [product, kooulPlateforms, aimerPlateforms, glovoPlatform, plateforms]);

    const getResolvedProductPlatformFlags = useMemo(() => {
        const productPlateforms = Array.isArray(product?.plateforms) ? product.plateforms : [];

        return productPlateforms.reduce((flags, platformEntry) => {
            const platformId = normalizeId(platformEntry?._id || platformEntry?.id || platformEntry?.plateform_id || platformEntry?.value);
            const resolvedPlatform = platformId
                ? aggregatedPlatforms.find((platform) => normalizeId(platform?._id || platform?.id || platform?.plateform_id || platform?.value) === platformId)
                : null;
            const { typeVal, nameVal } = extractPlatformTypeName(resolvedPlatform || platformEntry);
            const typeKey = normalizePlatformKey(typeVal);
            const nameKey = normalizePlatformKey(nameVal);

            if (typeKey === 'glovo_sisters' || nameKey.includes('glovo_sister') || nameKey.includes('glovo_sis')) {
                flags.hasGlovoSisters = true;
            } else if (typeKey === 'glovo' || nameKey.includes('glovo')) {
                flags.hasGlovo = true;
            }

            return flags;
        }, { hasGlovo: false, hasGlovoSisters: false });
    }, [aggregatedPlatforms, product]);

    const glovoSistersPlatform = useMemo(() => {
        if (!Array.isArray(aggregatedPlatforms)) return null;
        return aggregatedPlatforms.find(p => {
            const { typeVal, nameVal } = extractPlatformTypeName(p);
            const typeKey = normalizePlatformKey(typeVal);
            const nameKey = normalizePlatformKey(nameVal);
            return typeKey === 'glovo_sisters' || nameKey.includes('glovo_sister') || nameKey.includes('glovo_sis');
        }) || null;
    }, [aggregatedPlatforms]);

    // Glovo Sisters addon options (filter addons with plateforms_keys matching glovoSistersPlatform)
    const glovoSistersAddonOptions = useMemo(() => {
        if (!Array.isArray(productAddons) || !glovoSistersPlatform) return [];

        const glovoSistersPlatformId = normalizeId(glovoSistersPlatform._id || glovoSistersPlatform.id);

        const portalAddons = productAddons.filter(addon => {
            if (!Array.isArray(addon.plateforms_keys)) return false;
            return addon.plateforms_keys.some(pk => normalizeId(pk.plateform_id || pk.plateform_id?.$oid) === glovoSistersPlatformId && pk.remote_id);
        });

        return portalAddons.map(addon => {
            const addonId = normalizeId(addon._id || addon._id?.$oid);
            const portalKey = addon.plateforms_keys?.find(pk => normalizeId(pk.plateform_id || pk.plateform_id?.$oid) === glovoSistersPlatformId && pk.remote_id);
            const remoteId = portalKey?.remote_id || '';
            const options = Array.isArray(addon.options) ? addon.options.slice(0,5) : [];
            const optionNames = options.map(opt => opt.name).filter(Boolean);

            return {
                value: addonId,
                label: addon.name || 'Addon',
                remoteId: remoteId,
                optionNames: optionNames,
                fullAddon: addon
            };
        });
    }, [productAddons, glovoSistersPlatform]);
    
    // Selected Glovo addons value
    const selectedGlovoAddonsValue = useMemo(() => 
        glovoAddonOptions.filter(opt => selectedGlovoAddonIds.includes(opt.value)),
        [glovoAddonOptions, selectedGlovoAddonIds]
    );

    const getCategoryCreatedAtMs = useCallback((category) => {
        const rawCreatedAt = category?.createdAt?.$date || category?.createdAt;
        const createdAtMs = rawCreatedAt ? new Date(rawCreatedAt).getTime() : 0;
        return Number.isFinite(createdAtMs) ? createdAtMs : 0;
    }, []);

    const dedupeCategoriesByTypeCatalogAndRemote = useCallback((categories, getTypeFn) => {
        const seen = new Set();

        return categories.filter((cat) => {
            const type = `${getTypeFn(cat)}`.toLowerCase().trim();
            const catalogId = `${cat?.catalog_id || cat?.catalogId || cat?._catalogId || ''}`.trim();
            const remotePlatformId = `${cat?.remote_plateform_id || cat?.remotePlatformId || cat?.remote_platform_id || ''}`.trim();

            const uniqueKey = remotePlatformId
                ? `${type}|${catalogId}|${remotePlatformId}`
                : `${type}|${catalogId}|missing_remote|${normalizeId(cat?._id || cat?.id)}`;

            if (seen.has(uniqueKey)) return false;
            seen.add(uniqueKey);
            return true;
        });
    }, []);
    
    // Glovo category options (filtered by selected catalog)
    const glovoCategoryOptions = useMemo(() => {
        if (!selectedGlovoCatalogId || !Array.isArray(productCategories)) {
            return [];
        }
        
        const filtered = productCategories.filter(cat => {
            if (cat.type !== 'glovo') return false;
            const catalogId = cat.catalog_id || cat.catalogId || cat._catalogId;
            return String(catalogId) === selectedGlovoCatalogId;
        });

        const sorted = [...filtered].sort((a, b) => getCategoryCreatedAtMs(b) - getCategoryCreatedAtMs(a));
        const deduped = dedupeCategoriesByTypeCatalogAndRemote(sorted, (cat) => cat?.type || '');

        return deduped.map(cat => ({
            value: normalizeId(cat._id || cat.id),
            label: cat.name || 'Catégorie'
        }));
    }, [selectedGlovoCatalogId, productCategories, getCategoryCreatedAtMs, dedupeCategoriesByTypeCatalogAndRemote]);

    const glovoDescriptionLength = useMemo(() => glovoDescriptionDraft.length, [glovoDescriptionDraft]);
    const isGlovoDescriptionCompliant = useMemo(
        () => isDescriptionCompliant(glovoDescriptionDraft),
        [glovoDescriptionDraft]
    );
    const parsePriceDraft = useCallback((value) => {
        const raw = `${value ?? ''}`.trim().replace(',', '.');
        if (!raw) return Number.NaN;
        const parsed = Number(raw);
        return Number.isFinite(parsed) ? parsed : Number.NaN;
    }, []);
    const glovoParsedPrice = useMemo(() => parsePriceDraft(glovoPriceDraft), [glovoPriceDraft, parsePriceDraft]);
    const isGlovoPriceValid = useMemo(() => Number.isFinite(glovoParsedPrice) && glovoParsedPrice >= 0, [glovoParsedPrice]);

    const getProductCategoryType = useCallback((category) => {
        return `${category?.type || category?.additionnal_data?.type || category?.additional_data?.type || ''}`
            .toLowerCase()
            .trim();
    }, []);

    // Glovo Sisters catalog options (distinct catalog_id from productCategories type="glovo_sisters")
    const glovoSistersCatalogOptions = useMemo(() => {
        if (!Array.isArray(productCategories)) return [];

        const existingCatalogIds = new Set();
        if (product?.plateforms && Array.isArray(product.plateforms)) {
            product.plateforms.forEach(platform => {
                if (Array.isArray(platform.roadmap)) {
                    platform.roadmap.forEach(roadmapEntry => {
                        if (roadmapEntry.type === 'glovo_sisters' && roadmapEntry.data?.catalogIds) {
                            roadmapEntry.data.catalogIds.forEach(catId => existingCatalogIds.add(String(catId)));
                        }
                    });
                }
            });
        }

        const glovoCats = productCategories.filter(cat => getProductCategoryType(cat) === 'glovo_sisters');
        const catalogMap = new Map();
        glovoCats.forEach(cat => {
            const catalogId = cat.catalog_id || cat.catalogId || cat._catalogId;
            if (catalogId && !catalogMap.has(String(catalogId))) catalogMap.set(String(catalogId), cat);
        });

        return Array.from(catalogMap.entries())
            .filter(([catalogId]) => !existingCatalogIds.has(String(catalogId)))
            .map(([catalogId, firstRecord]) => {
                let storeId = firstRecord.store_id;
                if (storeId?.$oid) storeId = storeId.$oid;
                storeId = normalizeId(storeId);

                const store = stores.find(s => {
                    const sId = normalizeId(s._id || s.id);
                    const sValue = normalizeId(s.value);
                    const sRawId = normalizeId(s.rawId);
                    return sId === storeId || sValue === storeId || sRawId === storeId;
                });

                const storeName = store ? (store.name || store.label) : null;
                const storeCode = store ? store.storeCode : null;

                return {
                    value: catalogId,
                    label: storeName ? `Catalog ${catalogId} - ${storeName}` : `Catalog ${catalogId}`,
                    storeName,
                    storeId,
                    storeCode
                };
            });
    }, [productCategories, stores, product, getProductCategoryType]);

    // Glovo Sisters category options (filtered by selected catalog)
    const glovoSistersCategoryOptions = useMemo(() => {
        if (!selectedGlovoSistersCatalogId || !Array.isArray(productCategories)) return [];
        const filtered = productCategories.filter(cat => {
            if (getProductCategoryType(cat) !== 'glovo_sisters') return false;
            const catalogId = cat.catalog_id || cat.catalogId || cat._catalogId;
            return String(catalogId) === selectedGlovoSistersCatalogId;
        });

        const sorted = [...filtered].sort((a, b) => getCategoryCreatedAtMs(b) - getCategoryCreatedAtMs(a));
        const deduped = dedupeCategoriesByTypeCatalogAndRemote(sorted, getProductCategoryType);

        return deduped.map(cat => ({ value: normalizeId(cat._id || cat.id), label: cat.name || 'Catégorie' }));
    }, [selectedGlovoSistersCatalogId, productCategories, getProductCategoryType, getCategoryCreatedAtMs, dedupeCategoriesByTypeCatalogAndRemote]);

    const selectedGlovoSistersAddonsValue = useMemo(() => glovoSistersAddonOptions.filter(opt => selectedGlovoSistersAddonIds.includes(opt.value)), [glovoSistersAddonOptions, selectedGlovoSistersAddonIds]);

    const glovoSistersDescriptionLength = useMemo(() => glovoSistersDescriptionDraft.length, [glovoSistersDescriptionDraft]);
    const isGlovoSistersDescriptionCompliant = useMemo(() => isDescriptionCompliant(glovoSistersDescriptionDraft), [glovoSistersDescriptionDraft]);
    const glovoSistersParsedPrice = useMemo(() => parsePriceDraft(glovoSistersPriceDraft), [glovoSistersPriceDraft, parsePriceDraft]);
    const isGlovoSistersPriceValid = useMemo(() => Number.isFinite(glovoSistersParsedPrice) && glovoSistersParsedPrice >= 0, [glovoSistersParsedPrice]);
    const selectedProductIdsSet = useMemo(
        () => new Set((Array.isArray(selectedProductIds) ? selectedProductIds : []).map((item) => normalizeId(item)).filter(Boolean)),
        [selectedProductIds]
    );

    const productHasPlatformType = useCallback((targetProduct, targetType) => {
        const productPlateforms = Array.isArray(targetProduct?.plateforms) ? targetProduct.plateforms : [];

        return productPlateforms.some((platformEntry) => {
            const { typeVal, nameVal } = extractPlatformTypeName(platformEntry);
            const typeKey = normalizePlatformKey(typeVal);
            const nameKey = normalizePlatformKey(nameVal);

            if (targetType === 'glovo_sisters') {
                return typeKey === 'glovo_sisters' || nameKey.includes('glovo_sister') || nameKey.includes('glovo_sis');
            }

            if (targetType === 'glovo') {
                return typeKey === 'glovo' || nameKey.includes('glovo');
            }

            return false;
        });
    }, []);

    const bulkGlovoCandidates = useMemo(() => {
        if (!selectedProductIdsSet.size || !Array.isArray(allProducts)) {
            return [];
        }

        return allProducts.filter((item) => {
            const itemId = normalizeId(item?._id || item?.id);
            if (!itemId || !selectedProductIdsSet.has(itemId)) {
                return false;
            }

            return !productHasPlatformType(item, 'glovo');
        });
    }, [allProducts, productHasPlatformType, selectedProductIdsSet]);

    const bulkGlovoSistersCandidates = useMemo(() => {
        if (!selectedProductIdsSet.size || !Array.isArray(allProducts)) {
            return [];
        }

        return allProducts.filter((item) => {
            const itemId = normalizeId(item?._id || item?.id);
            if (!itemId || !selectedProductIdsSet.has(itemId)) {
                return false;
            }

            return !productHasPlatformType(item, 'glovo_sisters');
        });
    }, [allProducts, productHasPlatformType, selectedProductIdsSet]);

    const bulkSiteCandidates = useMemo(() => {
        if (!selectedProductIdsSet.size || !Array.isArray(allProducts)) {
            return [];
        }

        return allProducts.filter((item) => {
            const itemId = normalizeId(item?._id || item?.id);
            return itemId && selectedProductIdsSet.has(itemId);
        });
    }, [allProducts, selectedProductIdsSet]);

    const saveRecentPlatformSelection = useCallback((platformKey, payload) => {
        setRecentPlatformSelections((previous) => {
            const next = normalizeRecentSelections(previous);
            const platformSelections = next[platformKey] || createEmptyRecentSelections()[platformKey];

            platformSelections.stores = upsertRecentSelectionEntries(platformSelections.stores, payload?.store ? [payload.store] : []);
            platformSelections.catalogs = upsertRecentSelectionEntries(platformSelections.catalogs, payload?.catalog ? [payload.catalog] : []);
            platformSelections.categories = upsertRecentSelectionEntries(platformSelections.categories, payload?.category ? [payload.category] : []);
            platformSelections.addons = upsertRecentSelectionEntries(platformSelections.addons, payload?.addons || []);

            next[platformKey] = platformSelections;
            writeRecentPlatformSelections(next);
            return next;
        });
    }, []);

    const getRecentBadgeItems = useCallback((platformKey, bucketKey, options, extraFilter = null) => {
        const recentEntries = recentPlatformSelections?.[platformKey]?.[bucketKey];
        const optionMap = new Map(
            (Array.isArray(options) ? options : []).map((option) => [normalizeId(option?.value), option])
        );

        return (Array.isArray(recentEntries) ? recentEntries : [])
            .map((entry) => {
                const option = optionMap.get(normalizeId(entry?.value));
                if (!option) return null;
                if (typeof extraFilter === 'function' && !extraFilter(option, entry)) return null;
                return { ...entry, option };
            })
            .filter(Boolean)
            .slice(0, 8);
    }, [recentPlatformSelections]);

    const glovoRecentCatalogBadges = useMemo(
        () => getRecentBadgeItems('glovo', 'catalogs', glovoCatalogOptions),
        [getRecentBadgeItems, glovoCatalogOptions]
    );

    const glovoRecentCategoryBadges = useMemo(
        () => getRecentBadgeItems(
            'glovo',
            'categories',
            glovoCategoryOptions,
            (option, entry) => !selectedGlovoCatalogId || `${entry?.catalogId || ''}` === `${selectedGlovoCatalogId}`
        ),
        [getRecentBadgeItems, glovoCategoryOptions, selectedGlovoCatalogId]
    );

    const glovoRecentAddonBadges = useMemo(
        () => getRecentBadgeItems('glovo', 'addons', glovoAddonOptions),
        [getRecentBadgeItems, glovoAddonOptions]
    );

    const glovoRecentStoreBadges = useMemo(() => {
        const recentEntries = recentPlatformSelections?.glovo?.stores;

        return (Array.isArray(recentEntries) ? recentEntries : [])
            .map((entry) => {
                const matchingCatalog = glovoCatalogOptions.find((option) => {
                    if (entry?.catalogId && `${option?.value}` === `${entry.catalogId}`) {
                        return true;
                    }

                    return normalizeId(option?.storeId) === normalizeId(entry?.value);
                });

                if (!matchingCatalog) return null;
                return { ...entry, option: matchingCatalog };
            })
            .filter(Boolean)
            .slice(0, 8);
    }, [glovoCatalogOptions, recentPlatformSelections]);

    const glovoSistersRecentCatalogBadges = useMemo(
        () => getRecentBadgeItems('glovo_sisters', 'catalogs', glovoSistersCatalogOptions),
        [getRecentBadgeItems, glovoSistersCatalogOptions]
    );

    const glovoSistersRecentCategoryBadges = useMemo(
        () => getRecentBadgeItems(
            'glovo_sisters',
            'categories',
            glovoSistersCategoryOptions,
            (option, entry) => !selectedGlovoSistersCatalogId || `${entry?.catalogId || ''}` === `${selectedGlovoSistersCatalogId}`
        ),
        [getRecentBadgeItems, glovoSistersCategoryOptions, selectedGlovoSistersCatalogId]
    );

    const glovoSistersRecentAddonBadges = useMemo(
        () => getRecentBadgeItems('glovo_sisters', 'addons', glovoSistersAddonOptions),
        [getRecentBadgeItems, glovoSistersAddonOptions]
    );

    const glovoSistersRecentStoreBadges = useMemo(() => {
        const recentEntries = recentPlatformSelections?.glovo_sisters?.stores;

        return (Array.isArray(recentEntries) ? recentEntries : [])
            .map((entry) => {
                const matchingCatalog = glovoSistersCatalogOptions.find((option) => {
                    if (entry?.catalogId && `${option?.value}` === `${entry.catalogId}`) {
                        return true;
                    }

                    return normalizeId(option?.storeId) === normalizeId(entry?.value);
                });

                if (!matchingCatalog) return null;
                return { ...entry, option: matchingCatalog };
            })
            .filter(Boolean)
            .slice(0, 8);
    }, [glovoSistersCatalogOptions, recentPlatformSelections]);
    const resetPlateformRows = useMemo(() => {
        const productPlateforms = Array.isArray(product?.plateforms) ? product.plateforms : [];
        const allPlateforms = Array.isArray(plateforms) ? plateforms : [];
        const plateformsById = new Map(
            allPlateforms.map((item) => {
                const id = normalizeId(item?._id || item?.id || item?.plateform_id || item?.value);
                return [id, item];
            })
        );

        return productPlateforms
            .map((entry, index) => {
                const plateformId = normalizeId(entry?.plateform_id || entry?._id || entry?.id || entry?.value);
                const mappedPlateform = plateformsById.get(plateformId);
                return {
                    key: `${plateformId || 'unknown'}-${index}`,
                    plateformId,
                    plateformName: mappedPlateform?.name || entry?.name || plateformId || 'Plateforme inconnue',
                };
            })
            .filter((row) => Boolean(row.plateformId));
    }, [product?.plateforms, plateforms]);

    const marjanePlateformIds = new Set(
        (Array.isArray(plateforms) ? plateforms : [])
            .filter((plateform) => `${plateform?.type ?? ''}`.toLowerCase() === 'marjanemall')
            .map((plateform) => normalizeId(plateform?._id || plateform?.id || plateform?.plateform_id || plateform?.value))
            .filter(Boolean)
    );

    const hasMarjane = Array.isArray(product?.plateforms) && product.plateforms.some((entry) => {
        const productPlateformId = normalizeId(entry?.plateform_id || entry?._id || entry?.id || entry?.value);
        return productPlateformId && marjanePlateformIds.has(productPlateformId);
    });

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    // Recursive component to render category tree
    const renderKooulCategoryTree = (categories, level = 0) => {
        if (!Array.isArray(categories) || categories.length === 0) {
            return null;
        }

        return categories.map((category) => {
            const isExpanded = expandedKooulNodes.has(category.id);
            const isLeaf = isLeafCategory(category);
            const isSelected = selectedKooulLeafCategory?.id === category.id;
            const hasChildren = category.children && category.children.length > 0;

            return (
                <React.Fragment key={category.id}>
                    <ListItem disablePadding sx={{ pl: level * 3 }}>
                        <ListItemButton
                            onClick={() => {
                                if (isLeaf) {
                                    selectKooulLeafCategory(category);
                                } else {
                                    toggleKooulNode(category.id);
                                }
                            }}
                            selected={isSelected}
                            sx={{
                                bgcolor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                                '&:hover': {
                                    bgcolor: isSelected ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)',
                                },
                                borderLeft: isSelected ? '3px solid #1976d2' : 'none',
                            }}
                        >
                            {hasChildren && (
                                <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                </Box>
                            )}
                            {!hasChildren && (
                                <Box sx={{ mr: 1, ml: 3, display: 'flex', alignItems: 'center' }}>
                                    <ChevronRightIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                </Box>
                            )}
                            <ListItemText 
                                primary={category.name} 
                                primaryTypographyProps={{
                                    fontWeight: isSelected ? 600 : 400,
                                    fontSize: 14,
                                }}
                            />
                            {isLeaf && (
                                <Chip 
                                    label="Sélectionnable" 
                                    size="small" 
                                    color={isSelected ? "primary" : "default"}
                                    sx={{ ml: 1, fontSize: 11, height: 20 }}
                                />
                            )}
                            {isSelected && (
                                <CheckCircleIcon sx={{ ml: 1, color: 'primary.main', fontSize: 20 }} />
                            )}
                        </ListItemButton>
                    </ListItem>
                    {hasChildren && (
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            {renderKooulCategoryTree(category.children, level + 1)}
                        </Collapse>
                    )}
                </React.Fragment>
            );
        });
    };

    const renderOptionWithKey = (props, option) => (
        <li {...props} key={normalizeId(option?.value)}>
            {option?.label || ''}
        </li>
    );

    const renderQuickPickBadges = (title, items, activeValue, onClick) => {
        if (!Array.isArray(items) || items.length === 0) {
            return null;
        }

        return (
            <div className="mb-3">
                <div className="small text-muted mb-2">{title}</div>
                <div className="d-flex flex-wrap gap-2">
                    {items.map((item) => {
                        const badgeValue = normalizeId(item?.option?.value || item?.value);
                        const isActive = Array.isArray(activeValue)
                            ? activeValue.includes(badgeValue)
                            : normalizeId(activeValue) === badgeValue;

                        return (
                            <button
                                key={`${title}-${badgeValue}`}
                                type="button"
                                className={`btn btn-sm ${isActive ? 'btn-primary' : 'btn-outline-secondary'}`}
                                onClick={() => onClick(item)}
                            >
                                {item?.label || item?.option?.label || item?.option?.storeName || item?.value}
                                {item?.count ? ` (${item.count})` : ''}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    };

    // Handler for Glovo description change
    const handleGlovoDescriptionChange = (e) => {
        console.log('[Glovo Description] onChange triggered', {
            value: e.target.value,
            length: e.target.value.length,
            eventType: e.type,
            currentValue: glovoDescriptionDraft
        });
        setGlovoDescriptionDraft(e.target.value);
    };
    
    // Handler for Instagram description change
    const handleInstagramDescriptionChange = (e) => {
        console.log('[Instagram Description] onChange triggered', {
            value: e.target.value,
            length: e.target.value.length,
            eventType: e.type,
            currentValue: instagramDescriptionDraft
        });
        setInstagramDescriptionDraft(e.target.value);
    };
    
    // Handler for site fees change
    const handleSiteFeesChange = (e) => {
        setLocalSiteFees(e.target.value);
    };

    const handleSitePriceChange = (e) => {
        setLocalSitePrice(e.target.value);
    };

    const sitePublishSettings = {
        vaseInclusion: localSiteVase,
        websiteDelivery: {
            express_enabled: localSiteExpress,
            express_zones: localSiteExpress ? localSiteExpressZones : [],
            morocco_enabled: localSiteMorocco,
        },
    };
    const siteTotalPrice = Math.round((Number(localSitePrice) + Number(localSiteFees || 0)) * 100) / 100;

    const handleAimerSubmit = async () => {
        if (!localAimerCategoryIds.length) return;
        
        setIsAimerSubmitting(true);
        try {
            // Find the selected categories from options
            const selectedCategories = aimerCategoryOptions.filter(opt => 
                localAimerCategoryIds.includes(opt.value)
            );
            await onSubmitAimer(selectedCategories);
            onClose();
        } catch (error) {
            console.error('Error submitting to Aimer:', error);
        } finally {
            setIsAimerSubmitting(false);
        }
    };
    
    const handleKooulSubmit = async () => {
        if (!localKooulPlateformId || !selectedKooulLeafCategory) return;
        
        setIsKooulSubmitting(true);
        try {
            // Build category object for submission
            const selectedCategories = [{
                value: selectedKooulLeafCategory.id,
                label: selectedKooulLeafCategory.name,
            }];
            await onSubmitKooul(localKooulPlateform, selectedCategories);
            onClose();
        } catch (error) {
            console.error('Error submitting to Kooul:', error);
        } finally {
            setIsKooulSubmitting(false);
        }
    };

    const handleMarjaneSubmit = async () => {
        if (typeof onSubmitMarjane !== 'function') return;
        if (!selectedMarjaneLeafCategory) {
            toast.error('Veuillez sélectionner une catégorie enfant Marjane Mall.');
            return;
        }

        if (!selectedMarjaneLeafCategory.remoteId) {
            toast.error('La catégorie sélectionnée ne contient pas de remote_id.');
            return;
        }

        setIsMarjaneSubmitting(true);
        try {
            await onSubmitMarjane({
                id: selectedMarjaneLeafCategory.id,
                name: selectedMarjaneLeafCategory.name,
                remoteId: selectedMarjaneLeafCategory.remoteId,
            });
            onClose();
        } catch (error) {
            console.error('Error submitting to MarjaneMall:', error);
        } finally {
            setIsMarjaneSubmitting(false);
        }
    };
    
    const handleSiteSubmit = async () => {
        if (!localSiteCategoryIds.length) return;
        
        setIsSiteSubmitting(true);
        try {
            const selectedCategories = siteCategoryOptions.filter(opt => 
                localSiteCategoryIds.includes(opt.value)
            );
            await onSubmitSite(selectedCategories, localSiteFees, {
                price: localSitePrice,
                inStock: localSiteInStock,
                ...sitePublishSettings,
            });
            onClose();
        } catch (error) {
            console.error('Error submitting to Site:', error);
        } finally {
            setIsSiteSubmitting(false);
        }
    };

    const handleBulkSiteSubmit = async () => {
        if (!localSiteCategoryIds.length || bulkSiteCandidates.length < 2) return;

        setIsSiteSubmitting(true);
        try {
            const selectedCategories = siteCategoryOptions.filter(opt =>
                localSiteCategoryIds.includes(opt.value)
            );
            await onSubmitSite(selectedCategories, localSiteFees, {
                inStock: localSiteInStock,
                products: bulkSiteCandidates,
                ...sitePublishSettings,
            });
            onClose();
        } catch (error) {
            console.error('Error submitting bulk to Site:', error);
        } finally {
            setIsSiteSubmitting(false);
        }
    };
    
    const handleInstagramSubmit = async () => {
        setIsInstagramSubmitting(true);
        try {
            await onSubmitInstagram(instagramDescriptionDraft);
            onClose();
        } catch (error) {
            console.error('Error submitting to Instagram:', error);
        } finally {
            setIsInstagramSubmitting(false);
        }
    };

    const handleDolibarrSubmit = async () => {
        if (typeof onSubmitDolibarr !== 'function') return;
        if (!localDolibarrCompanyId) {
            toast.error('Veuillez sélectionner une société Dolibarr.');
            return;
        }

        setIsDolibarrSubmitting(true);
        try {
            await onSubmitDolibarr(localDolibarrCompanyId, {
                price: dolibarrPriceDraft,
                ref: dolibarrFinalRef,
            });
            onClose();
        } catch (error) {
            console.error('Error submitting to Dolibarr:', error);
        } finally {
            setIsDolibarrSubmitting(false);
        }
    };

    const handleDolibarrStockSubmit = async () => {
        if (typeof onUpdateDolibarrStock !== 'function') return;
        if (!localDolibarrCompanyId) {
            toast.error('Veuillez sélectionner une société compta.');
            return;
        }

        const warehouseId = Number(dolibarrWarehouseId);
        const targetStock = Number(dolibarrTargetStock);
        if (!Number.isFinite(warehouseId) || warehouseId <= 0) {
            toast.error('Veuillez sélectionner un dépôt.');
            return;
        }
        if (!Number.isFinite(targetStock)) {
            toast.error('Veuillez saisir un stock cible valide.');
            return;
        }

        setIsDolibarrStockSubmitting(true);
        try {
            const payload = await onUpdateDolibarrStock(localDolibarrCompanyId, {
                warehouse_id: warehouseId,
                target_stock: targetStock,
            });
            setDolibarrStockState(payload);
            const stockEntries = Array.isArray(payload?.stock) ? payload.stock : [];
            const selectedEntry = stockEntries.find((entry) => `${entry?.warehouse_id || entry?.id || entry?.fk_entrepot || ''}` === `${warehouseId}`) || stockEntries[0] || null;
            const refreshedStock = selectedEntry
                ? Number(selectedEntry?.stock_reel ?? selectedEntry?.real ?? selectedEntry?.qty ?? selectedEntry?.stock ?? 0)
                : targetStock;
            setDolibarrTargetStock(Number.isFinite(refreshedStock) ? String(refreshedStock) : String(targetStock));
            toast.success(payload?.message || 'Stock compta mis à jour.');
        } catch (error) {
            console.error('Error submitting Dolibarr stock update:', error);
            toast.error(error?.response?.data?.message || error?.message || 'Erreur lors de la mise à jour du stock compta');
        } finally {
            setIsDolibarrStockSubmitting(false);
        }
    };

    // Fetch updated product data from server
    const fetchUpdatedProduct = async (productId) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_PEGASUS_API_URL}/product/${productId}`, {
                method: 'GET',
                headers: {
                    ...header().headers,
                }
            });
            
            if (!response.ok) {
                console.error('Failed to fetch updated product');
                return null;
            }
            
            const updatedProduct = await response.json();
            console.log('[PublierPlateformesModal] Product updated from server:', updatedProduct);
            return updatedProduct;
        } catch (error) {
            console.error('Error fetching updated product:', error);
            return null;
        }
    };

    const createPortalProduct = useCallback(async (productId, payload) => {
        const response = await fetch(`${process.env.REACT_APP_PEGASUS_API_URL}/glovo-portal/product/add/${productId}`, {
            method: 'POST',
            headers: {
                ...header().headers,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });

        const data = await response.json();
        if (!response.ok || !data?.success) {
            throw new Error(data?.error || 'Echec de publication');
        }

        return data;
    }, []);

    const checkPortalApproval = useCallback(async ({ plateformId, vendorId, commandId, productId }) => {
        for (let attempt = 1; attempt <= 5; attempt += 1) {
            try {
                await new Promise((resolve) => setTimeout(resolve, attempt > 1 ? 10000 : 2000));

                const checkResponse = await fetch(`${process.env.REACT_APP_PEGASUS_API_URL}/glovo-portal/commands/check`, {
                    method: 'POST',
                    headers: {
                        ...header().headers,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        plateformId,
                        vendorId,
                        commandId,
                        globalEntityId: 'GV_MA',
                        product_id: productId,
                    }),
                });

                const checkData = await checkResponse.json();

                if (checkData.success && checkData.command?.status === 'APPROVED') {
                    return { status: 'APPROVED', data: checkData };
                }

                if (checkData.command?.status === 'REJECTED') {
                    return { status: 'REJECTED', data: checkData };
                }
            } catch (error) {
                if (attempt === 5) {
                    return { status: 'CHECK_ERROR', error };
                }
            }
        }

        return { status: 'TIMEOUT' };
    }, []);

    const resolveGlovoProductDescription = useCallback((targetProduct) => {
        return String(
            targetProduct?.description ||
            targetProduct?.long_description ||
            targetProduct?.name ||
            ''
        ).trim();
    }, []);

    const resolveGlovoProductPrice = useCallback((targetProduct) => {
        const candidates = [
            targetProduct?.regular_price,
            targetProduct?.price,
            targetProduct?.sale_price,
            targetProduct?.discounted_price,
        ];

        for (const candidate of candidates) {
            const parsed = Number(candidate);
            if (Number.isFinite(parsed) && parsed > 0) {
                return parsed;
            }
        }

        return NaN;
    }, []);

    const buildGlovoPayload = useCallback((targetProduct = product, options = {}) => {
        const selectedCatalog = glovoCatalogOptions.find((opt) => opt.value === selectedGlovoCatalogId);
        const vendorId = selectedCatalog?.storeCode || '';
        const addonRemoteIds = selectedGlovoAddonsValue
            .map((addon) => addon.remoteId)
            .filter(Boolean);
        const useProductDefaults = options?.useProductDefaults === true;
        const description = useProductDefaults
            ? resolveGlovoProductDescription(targetProduct)
            : glovoDescriptionDraft;
        const price = useProductDefaults
            ? resolveGlovoProductPrice(targetProduct)
            : glovoParsedPrice;

        return {
            selectedCatalog,
            payload: {
                vendorId,
                catalogId: selectedGlovoCatalogId,
                categoryId: selectedGlovoCategoryId,
                description,
                productOptionIds: addonRemoteIds,
                price,
                globalEntityId: 'GV_MA',
            },
        };
    }, [
        glovoCatalogOptions,
        glovoDescriptionDraft,
        glovoParsedPrice,
        product,
        resolveGlovoProductDescription,
        resolveGlovoProductPrice,
        selectedGlovoAddonsValue,
        selectedGlovoCatalogId,
        selectedGlovoCategoryId,
    ]);

    const dolibarrWarehouseOptions = useMemo(() => {
        const warehouses = Array.isArray(dolibarrStockState?.warehouses) ? dolibarrStockState.warehouses : [];
        return warehouses.map((warehouse) => ({
            value: String(warehouse?.id || warehouse?.rowid || ''),
            label: warehouse?.label || warehouse?.ref || `Depot ${warehouse?.id || warehouse?.rowid || ''}`,
        })).filter((option) => option.value);
    }, [dolibarrStockState]);
    const dolibarrSelectedStockEntry = useMemo(() => {
        const stockEntries = Array.isArray(dolibarrStockState?.stock) ? dolibarrStockState.stock : [];
        if (!stockEntries.length) return null;
        return stockEntries.find((entry) => `${entry?.warehouse_id || entry?.id || entry?.fk_entrepot || ''}` === `${dolibarrWarehouseId}`) || stockEntries[0] || null;
    }, [dolibarrStockState, dolibarrWarehouseId]);
    const dolibarrCurrentStockValue = useMemo(() => {
        const value = Number(dolibarrSelectedStockEntry?.stock_reel ?? dolibarrSelectedStockEntry?.real ?? dolibarrSelectedStockEntry?.qty ?? dolibarrSelectedStockEntry?.stock ?? 0);
        return Number.isFinite(value) ? value : 0;
    }, [dolibarrSelectedStockEntry]);
    const dolibarrFinalRef = useMemo(() => {
        const prefix = sanitizeDolibarrRefPart(dolibarrRefPrefix);
        const base = sanitizeDolibarrRefPart(dolibarrRefBase);
        if (!prefix) return base;
        return sanitizeDolibarrRefPart(`${prefix}-${base}`).slice(0, 128);
    }, [dolibarrRefPrefix, dolibarrRefBase]);

    // Early return after all hooks
    if (!product) return null;

    // Check platform statuses
    const hasAimer = Array.isArray(product?.plateforms) && product.plateforms.some(p => {
        const type = `${p?.type ?? ''}`.toLowerCase();
        const name = `${p?.name ?? ''}`.toLowerCase();
        return type === 'aimer' || name.includes('aimer');
    });

    const hasKooul = Array.isArray(product?.plateforms) && product.plateforms.some(p => {
        const type = `${p?.type ?? ''}`.toLowerCase();
        const name = `${p?.name ?? ''}`.toLowerCase();
        return type === 'kooul' || name.startsWith('kooul_');
    });

    const hasSite = product?.woocommerce_id && product.woocommerce_id !== '';
    const hasInstagram = product?.instagram_post_id && product.instagram_post_id !== '';
    const hasDolibarr = dolibarrLinkedEntries.length > 0;
    const selectedDolibarrCompanyOption = dolibarrCompanyOptions.find((option) => option.value === localDolibarrCompanyId) || null;
    const selectedDolibarrLinkedEntry = dolibarrLinkedEntries.find((entry) => entry.companyId === localDolibarrCompanyId) || null;
    const isSelectedDolibarrLinked = Boolean(selectedDolibarrLinkedEntry);
    
    const hasGlovo = getResolvedProductPlatformFlags.hasGlovo;

    const hasGlovoSisters = getResolvedProductPlatformFlags.hasGlovoSisters;

    const handleResetPlateformDelete = async (plateformId) => {
        const productId = normalizeId(product?._id);
        if (!productId || !plateformId) return;

        setDeletingPlateformIds((prev) => {
            const next = new Set(prev);
            next.add(plateformId);
            return next;
        });

        try {
            const response = await fetch(
                `${process.env.REACT_APP_PEGASUS_API_URL}/product/${productId}/plateforms/${plateformId}`,
                {
                    method: 'DELETE',
                    headers: {
                        ...header().headers,
                    },
                }
            );

            let data = null;
            try {
                data = await response.json();
            } catch (e) {
                data = null;
            }

            if (!response.ok) {
                toast.error(data?.message || 'Impossible de supprimer la plateforme du produit.');
                return;
            }

            toast.success(data?.message || 'Plateforme supprimée du produit.');
            const updatedProduct = await fetchUpdatedProduct(productId);
            if (updatedProduct && onProductUpdated) {
                onProductUpdated(updatedProduct);
            }
        } catch (error) {
            console.error('Error deleting product plateform link:', error);
            toast.error('Erreur lors de la suppression de la plateforme.');
        } finally {
            setDeletingPlateformIds((prev) => {
                const next = new Set(prev);
                next.delete(plateformId);
                return next;
            });
        }
    };

    const handleGlovoSubmit = async () => {
        if (!product?._id || !selectedGlovoCatalogId || !selectedGlovoCategoryId) return;

        setIsGlovoSubmitting(true);
        try {
            const { selectedCatalog, payload } = buildGlovoPayload();
            const data = await createPortalProduct(product._id, payload);

            saveRecentPlatformSelection('glovo', {
                store: selectedCatalog?.storeId ? {
                    value: normalizeId(selectedCatalog.storeId),
                    label: selectedCatalog.storeName || selectedCatalog.label,
                    catalogId: selectedCatalog.value,
                } : null,
                catalog: selectedCatalog ? {
                    value: selectedCatalog.value,
                    label: selectedCatalog.label,
                    storeId: selectedCatalog.storeId,
                } : null,
                category: selectedGlovoCategoryId ? {
                    value: selectedGlovoCategoryId,
                    label: glovoCategoryOptions.find((opt) => opt.value === selectedGlovoCategoryId)?.label || selectedGlovoCategoryId,
                    catalogId: selectedGlovoCatalogId,
                } : null,
                addons: selectedGlovoAddonsValue.map((addon) => ({
                    value: addon.value,
                    label: addon.label,
                })),
            });

            toast.success('Produit envoy? ? Glovo. V?rification en arri?re-plan.');
            onClose();

            checkPortalApproval({
                plateformId: data.plateformId,
                vendorId: data.vendorId,
                commandId: data.command?.commandId,
                productId: data.productId,
            }).then(async (approval) => {
                if (approval.status === 'APPROVED') {
                    toast.success('Produit publie et approuve sur Glovo avec succes!');
                    const updatedProduct = await fetchUpdatedProduct(product._id);
                    if (updatedProduct && onProductUpdated) {
                        onProductUpdated(updatedProduct);
                    }
                    return;
                }

                if (approval.status === 'REJECTED') {
                    toast.error(`Produit rejete par Glovo: ${approval.data?.message || 'Raison inconnue'}`);
                    return;
                }

                toast.warning("Produit publie sur Glovo, mais impossible de confirmer l'approbation automatiquement.");
            }).catch((error) => {
                console.error('Background Glovo approval check failed:', error);
                toast.warning("Produit publie sur Glovo, mais impossible de confirmer l'approbation automatiquement.");
            });
        } catch (error) {
            console.error('Error publishing to Glovo:', error);
            toast.error(`Erreur lors de la publication sur Glovo: ${error.message || 'Erreur inconnue'}`);
        } finally {
            setIsGlovoSubmitting(false);
        }
    };

    const handleBulkGlovoSubmit = async () => {
        if (!bulkGlovoCandidates.length || !selectedGlovoCatalogId || !selectedGlovoCategoryId) return;

        setIsBulkGlovoSubmitting(true);
        const queue = bulkGlovoCandidates.map((item) => ({
            productId: normalizeId(item?._id || item?.id),
            productName: item?.name || normalizeId(item?._id || item?.id),
            status: 'pending',
        }));
        setBulkGlovoQueue(queue);

        try {
            const { selectedCatalog } = buildGlovoPayload();
            const nextQueue = [...queue];
            let successCount = 0;
            let errorCount = 0;

            for (let index = 0; index < bulkGlovoCandidates.length; index += 1) {
                const targetProduct = bulkGlovoCandidates[index];
                const targetProductId = normalizeId(targetProduct?._id || targetProduct?.id);

                nextQueue[index] = { ...nextQueue[index], status: 'in-progress' };
                setBulkGlovoQueue([...nextQueue]);

                try {
                    const { payload } = buildGlovoPayload(targetProduct, { useProductDefaults: true });
                    if (!payload.description) {
                        throw new Error('Description produit manquante');
                    }
                    if (!Number.isFinite(Number(payload.price)) || Number(payload.price) <= 0) {
                        throw new Error('Prix produit invalide');
                    }
                    const data = await createPortalProduct(targetProductId, payload);
                    const approval = await checkPortalApproval({
                        plateformId: data.plateformId,
                        vendorId: data.vendorId,
                        commandId: data.command?.commandId,
                        productId: data.productId,
                    });

                    if (approval.status === 'REJECTED') {
                        throw new Error(approval.data?.message || 'Produit rejete');
                    }

                    nextQueue[index] = { ...nextQueue[index], status: 'done' };
                    successCount += 1;

                    const updatedProduct = await fetchUpdatedProduct(targetProductId);
                    if (updatedProduct && onProductUpdated) {
                        onProductUpdated(updatedProduct);
                    }
                } catch (error) {
                    nextQueue[index] = { ...nextQueue[index], status: 'error', errorMsg: error.message || 'Erreur inconnue' };
                    errorCount += 1;
                }

                setBulkGlovoQueue([...nextQueue]);
            }

            saveRecentPlatformSelection('glovo', {
                store: selectedCatalog?.storeId ? {
                    value: normalizeId(selectedCatalog.storeId),
                    label: selectedCatalog.storeName || selectedCatalog.label,
                    catalogId: selectedCatalog.value,
                } : null,
                catalog: selectedCatalog ? {
                    value: selectedCatalog.value,
                    label: selectedCatalog.label,
                    storeId: selectedCatalog.storeId,
                } : null,
                category: selectedGlovoCategoryId ? {
                    value: selectedGlovoCategoryId,
                    label: glovoCategoryOptions.find((opt) => opt.value === selectedGlovoCategoryId)?.label || selectedGlovoCategoryId,
                    catalogId: selectedGlovoCatalogId,
                } : null,
                addons: selectedGlovoAddonsValue.map((addon) => ({
                    value: addon.value,
                    label: addon.label,
                })),
            });

            if (successCount > 0 && errorCount === 0) {
                toast.success(`${successCount} produit(s) publies sur Glovo.`);
            } else if (successCount > 0) {
                toast.warning(`${successCount} produit(s) publies, ${errorCount} erreur(s).`);
            } else {
                toast.error("Aucun produit n'a pu etre publie sur Glovo.");
            }
        } catch (error) {
            console.error('Error bulk publishing to Glovo:', error);
            toast.error(`Erreur lors de la publication en lot sur Glovo: ${error.message || 'Erreur inconnue'}`);
        } finally {
            setIsBulkGlovoSubmitting(false);
        }
    };

    const resolveGlovoSistersPublishContext = (targetProduct = product) => {
        const selectedCatalog = glovoSistersCatalogOptions.find(opt => opt.value === selectedGlovoSistersCatalogId);
        const storeIdCandidate = selectedCatalog?.storeId || selectedCatalog?.storeCode || null;
        let platformToUse = glovoSistersPlatform;

        if (!platformToUse && Array.isArray(targetProduct?.plateforms)) {
            platformToUse = targetProduct.plateforms.find(p => {
                const typeKey = normalizePlatformKey(p?.type);
                const nameKey = normalizePlatformKey(p?.name);
                if (typeKey === 'glovo_sisters' || nameKey.includes('glovo_sister') || nameKey.includes('glovo_sis')) return true;
                if (Array.isArray(p.store_ids) && storeIdCandidate) {
                    return p.store_ids.some(si => {
                        const sid = normalizeId(si?.store_id || si);
                        return sid === normalizeId(storeIdCandidate) || sid === normalizeId(selectedCatalog?.storeCode);
                    });
                }
                return false;
            }) || null;
        }

        if (!platformToUse && Array.isArray(aggregatedPlatforms)) {
            platformToUse = aggregatedPlatforms.find(p => {
                const { typeVal, nameVal } = extractPlatformTypeName(p);
                const typeKey = normalizePlatformKey(typeVal);
                const nameKey = normalizePlatformKey(nameVal);
                return typeKey === 'glovo_sisters' || nameKey.includes('glovo_sister') || nameKey.includes('glovo_sis');
            }) || null;
        }

        if (!platformToUse) {
            throw new Error('Impossible de trouver la plateforme Glovo Sisters associée.');
        }

        const plateformId = normalizeId(platformToUse._id || platformToUse.id);
        const add = platformToUse?.additionnal_data || platformToUse?.additional_data || {};
        const portal = add.portal || add.Portal || null;
        const storesMapped = portal?.stores_maped || portal?.stores_mapped || portal?.storesMapped || portal?.stores_map || add.stores_maped || add.stores_mapped || add.stores_map || null;

        if (!storesMapped) {
            throw new Error('vendorId not existing (platform mapping missing for glovo_sisters)');
        }

        let vendorId = '';
        if (storesMapped && typeof storesMapped === 'object' && !Array.isArray(storesMapped) && (storesMapped.remote_store_id || storesMapped.remoteId || storesMapped.remote_store)) {
            vendorId = String(storesMapped.remote_store_id || storesMapped.remoteId || storesMapped.remote_store);
        }

        if (!vendorId && Array.isArray(storesMapped)) {
            const internalStore = (Array.isArray(stores) ? stores : []).find(s => {
                const candidates = [s._id, s.value, s.id, s.rawId];
                return candidates.some(c => c && String(c) === String(storeIdCandidate));
            }) || null;

            const match = storesMapped.find(m => {
                const sid = String(m.store_id || m.storeId || '');
                const rid = String(m.remote_store_id || m.remoteId || '');
                if (storeIdCandidate && sid && String(sid) === String(storeIdCandidate)) return true;
                if (internalStore && sid && String(sid) === String(internalStore._id || internalStore.id || internalStore.value)) return true;
                if (storeIdCandidate && rid && String(rid) === String(storeIdCandidate)) return true;
                return false;
            }) || (storesMapped.length === 1 ? storesMapped[0] : null);

            if (match) {
                vendorId = String(match.remote_store_id || match.remoteId || match.remote_store || match.remote || '');
            }
        }

        if (!vendorId && storesMapped && typeof storesMapped === 'object') {
            const entries = Object.entries(storesMapped || {});
            const direct = storeIdCandidate && storesMapped[storeIdCandidate] ? [storeIdCandidate, storesMapped[storeIdCandidate]] : null;
            const entry = direct || entries.find(([key, value]) => {
                if (String(key) === String(storeIdCandidate)) return true;
                if (value && typeof value === 'object' && (value.remote_store_id || value.remoteId || value.remote_store)) return true;
                return String(value) === String(storeIdCandidate);
            }) || (entries.length === 1 ? entries[0] : null);

            if (entry) {
                const value = entry[1];
                vendorId = String((value && typeof value === 'object') ? (value.remote_store_id || value.remoteId || value.remote_store || value.remote || '') : value);
            }
        }

        if (!vendorId) {
            throw new Error('vendorId not existing');
        }

        return { selectedCatalog, plateformId, vendorId };
    };

    const buildGlovoSistersPayload = (targetProduct = product, options = {}) => {
        const { selectedCatalog, plateformId, vendorId } = resolveGlovoSistersPublishContext(targetProduct);
        const addonRemoteIds = selectedGlovoSistersAddonsValue
            .map(addon => addon.remoteId)
            .filter(Boolean);
        const fallbackDescription = `${targetProduct?.description || targetProduct?.long_description || ''}`.trim();
        const fallbackPrice = Number(targetProduct?.regular_price ?? targetProduct?.price);
        const description = options.useProductDefaults ? fallbackDescription : glovoSistersDescriptionDraft;
        const price = options.useProductDefaults ? fallbackPrice : glovoSistersParsedPrice;

        return {
            selectedCatalog,
            payload: {
                plateformId,
                vendorId,
                catalogId: selectedGlovoSistersCatalogId,
                categoryId: selectedGlovoSistersCategoryId,
                description,
                productOptionIds: addonRemoteIds,
                price,
                globalEntityId: 'GV_MA'
            }
        };
    };

    const createGlovoSistersProduct = async (targetProductId, payload) => {
        const response = await fetch(`${process.env.REACT_APP_PEGASUS_API_URL}/glovo-portal/product/add/${targetProductId}`, {
            method: 'POST',
            headers: {
                ...header().headers,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload)
        });
        const data = await response.json();
        if (!data?.success) {
            throw new Error(data?.error || 'Échec de publication Glovo Sisters');
        }
        return data;
    };

    const checkGlovoSistersApproval = async ({ plateformId, vendorId, commandId, productId }) => {
        if (!commandId) {
            return { status: 'UNKNOWN' };
        }

        const checkResponse = await fetch(`${process.env.REACT_APP_PEGASUS_API_URL}/glovo-portal/commands/check`, {
            method: 'POST',
            headers: {
                ...header().headers,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                plateformId,
                vendorId,
                commandId,
                globalEntityId: 'GV_MA',
                product_id: productId
            })
        });
        const checkData = await checkResponse.json();
        return {
            status: checkData?.command?.status || (checkData?.success ? 'PENDING' : 'UNKNOWN'),
            data: checkData,
        };
    };

    const handleBulkGlovoSistersSubmit = async () => {
        if (!bulkGlovoSistersCandidates.length || !selectedGlovoSistersCatalogId || !selectedGlovoSistersCategoryId) return;

        setIsBulkGlovoSistersSubmitting(true);
        const queue = bulkGlovoSistersCandidates.map((item) => ({
            productId: normalizeId(item?._id || item?.id),
            productName: item?.name || normalizeId(item?._id || item?.id),
            status: 'pending',
        }));
        setBulkGlovoSistersQueue(queue);

        try {
            const { selectedCatalog } = buildGlovoSistersPayload(product, { useProductDefaults: false });
            const nextQueue = [...queue];
            let successCount = 0;
            let errorCount = 0;

            for (let index = 0; index < bulkGlovoSistersCandidates.length; index += 1) {
                const targetProduct = bulkGlovoSistersCandidates[index];
                const targetProductId = normalizeId(targetProduct?._id || targetProduct?.id);

                nextQueue[index] = { ...nextQueue[index], status: 'in-progress' };
                setBulkGlovoSistersQueue([...nextQueue]);

                try {
                    const { payload } = buildGlovoSistersPayload(targetProduct, { useProductDefaults: true });
                    if (!payload.description || payload.description.length < 50 || payload.description.length > 200) {
                        throw new Error('Description produit invalide pour Glovo Sisters');
                    }
                    if (!Number.isFinite(Number(payload.price)) || Number(payload.price) <= 0) {
                        throw new Error('Prix produit invalide');
                    }

                    const data = await createGlovoSistersProduct(targetProductId, payload);
                    const approval = await checkGlovoSistersApproval({
                        plateformId: payload.plateformId || data.plateformId,
                        vendorId: data.vendorId || payload.vendorId,
                        commandId: data.command?.commandId,
                        productId: data.productId,
                    });

                    if (approval.status === 'REJECTED') {
                        throw new Error(approval.data?.message || 'Produit rejeté');
                    }

                    nextQueue[index] = { ...nextQueue[index], status: 'done' };
                    successCount += 1;

                    const updatedProduct = await fetchUpdatedProduct(targetProductId);
                    if (updatedProduct && onProductUpdated) {
                        onProductUpdated(updatedProduct);
                    }
                } catch (error) {
                    nextQueue[index] = { ...nextQueue[index], status: 'error', errorMsg: error.message || 'Erreur inconnue' };
                    errorCount += 1;
                }

                setBulkGlovoSistersQueue([...nextQueue]);
            }

            saveRecentPlatformSelection('glovo_sisters', {
                store: selectedCatalog?.storeId ? {
                    value: normalizeId(selectedCatalog.storeId),
                    label: selectedCatalog.storeName || selectedCatalog.label,
                    catalogId: selectedCatalog.value,
                } : null,
                catalog: selectedCatalog ? {
                    value: selectedCatalog.value,
                    label: selectedCatalog.label,
                    storeId: selectedCatalog.storeId,
                } : null,
                category: selectedGlovoSistersCategoryId ? {
                    value: selectedGlovoSistersCategoryId,
                    label: glovoSistersCategoryOptions.find((opt) => opt.value === selectedGlovoSistersCategoryId)?.label || selectedGlovoSistersCategoryId,
                    catalogId: selectedGlovoSistersCatalogId,
                } : null,
                addons: selectedGlovoSistersAddonsValue.map((addon) => ({
                    value: addon.value,
                    label: addon.label,
                })),
            });

            if (successCount > 0 && errorCount === 0) {
                toast.success(`${successCount} produit(s) publiés sur Glovo Sisters.`);
            } else if (successCount > 0) {
                toast.warning(`${successCount} produit(s) publiés, ${errorCount} erreur(s).`);
            } else {
                toast.error("Aucun produit n'a pu être publié sur Glovo Sisters.");
            }
        } catch (error) {
            console.error('Error bulk publishing to Glovo Sisters:', error);
            toast.error(`Erreur lors de la publication en lot sur Glovo Sisters: ${error.message || 'Erreur inconnue'}`);
        } finally {
            setIsBulkGlovoSistersSubmitting(false);
        }
    };

    const handleGlovoSistersSubmit = async () => {
        if (!product?._id || !selectedGlovoSistersCatalogId || !selectedGlovoSistersCategoryId) {
            toast.error('Veuillez sélectionner un catalogue et une catégorie Glovo Sisters avant de créer le produit.');
            return;
        }

        setIsGlovoSistersSubmitting(true);
        try {
            const addonRemoteIds = selectedGlovoSistersAddonsValue
                .map(addon => addon.remoteId)
                .filter(Boolean);

            const selectedCatalog = glovoSistersCatalogOptions.find(opt => opt.value === selectedGlovoSistersCatalogId);

            // Determine platform id: prefer detected glovoSistersPlatform, otherwise try to find one in product.plateforms matching store
            let platformToUse = glovoSistersPlatform;
            console.log('[GlovoSisters] product.plateforms:', product?.plateforms);
            console.log('[GlovoSisters] initial glovoSistersPlatform:', glovoSistersPlatform);
            if (!platformToUse && Array.isArray(product?.plateforms)) {
                const storeIdCandidate = selectedCatalog?.storeId || selectedCatalog?.storeCode;
                platformToUse = product.plateforms.find(p => {
                    const typeKey = normalizePlatformKey(p?.type);
                    const nameKey = normalizePlatformKey(p?.name);
                    if (typeKey === 'glovo_sisters' || nameKey.includes('glovo_sister') || nameKey.includes('glovo_sis')) return true;
                    if (Array.isArray(p.store_ids) && storeIdCandidate) {
                        return p.store_ids.some(si => {
                            const sid = normalizeId(si?.store_id || si);
                            return sid === normalizeId(storeIdCandidate) || sid === normalizeId(selectedCatalog?.storeCode);
                        });
                    }
                    return false;
                }) || null;
            }

            if (!platformToUse) {
                console.log('[GlovoSisters] No platformToUse found after fallback search; trying aggregatedPlatforms');
                if (Array.isArray(aggregatedPlatforms)) {
                    const found = aggregatedPlatforms.find(p => {
                        const { typeVal, nameVal } = extractPlatformTypeName(p);
                        const typeKey = normalizePlatformKey(typeVal);
                        const nameKey = normalizePlatformKey(nameVal);
                        return typeKey === 'glovo_sisters' || nameKey.includes('glovo_sister') || nameKey.includes('glovo_sis');
                    });
                    if (found) {
                        platformToUse = found;
                        console.log('[GlovoSisters] Found platform in aggregatedPlatforms', found);
                    }
                }

                if (!platformToUse) {
                    console.log('[GlovoSisters] No platformToUse found in any aggregated sources');
                    toast.error('Impossible de trouver la plateforme Glovo Sisters associée. Veuillez configurer la plateforme avant de publier.');
                    setIsGlovoSistersSubmitting(false);
                    return;
                }
            }

            const plateformId = normalizeId(platformToUse._id || platformToUse.id);

            // Determine vendorId from platform mapping for glovo_sisters ONLY
            let vendorId = '';
            try {
                const storeIdCandidate = selectedCatalog?.storeId || selectedCatalog?.storeCode || null;
                // mapping lives under additionnal_data.portal.stores_maped per spec
                const add = platformToUse?.additionnal_data || platformToUse?.additional_data || {};
                const portal = add.portal || add.Portal || null;
                // accept multiple naming variants for the stores mapping and for the
                // singular `stores_map` key used in some platform docs
                const storesMapped = portal?.stores_maped || portal?.stores_mapped || portal?.storesMapped || portal?.stores_map || add.stores_maped || add.stores_mapped || add.stores_map || null;
                console.log('[GlovoSisters] platform stores_maped:', storesMapped, 'storeIdCandidate:', storeIdCandidate);

                if (!storesMapped) {
                    toast.error('vendorId not existing (platform mapping missing for glovo_sisters)');
                    setIsGlovoSistersSubmitting(false);
                    return;
                }

                // If storesMapped is a single mapping object with explicit keys
                // `{ remote_store_id, store_id }`, prefer the `remote_store_id` value.
                if (storesMapped && typeof storesMapped === 'object' && (storesMapped.remote_store_id || storesMapped.remoteId || storesMapped.remote_store)) {
                    vendorId = String(storesMapped.remote_store_id || storesMapped.remoteId || storesMapped.remote_store);
                }

                // If this platform is Glovo Sisters, prefer taking the vendorId
                // directly from the mapping. Many Glovo Sisters platform docs
                // only contain a single mapping entry; in that case use its
                // `remote_store_id` (or common variants) without attempting any local match.
                const platTypeKey = normalizePlatformKey(platformToUse?.type || '');
                const platNameKey = normalizePlatformKey(platformToUse?.name || '');
                if (platTypeKey === 'glovo_sisters' || platNameKey.includes('glovo_sis')) {
                    // if vendorId already resolved above from top-level mapping, keep it
                    if (vendorId) {
                        // already set
                    } else {
                        let directVendor = '';
                        if (Array.isArray(storesMapped) && storesMapped.length > 0) {
                        const e = storesMapped[0] || {};
                        directVendor = String(e.remote_store_id || e.remoteId || e.remote_store || e.remote || e);
                    } else if (storesMapped && typeof storesMapped === 'object') {
                        const entries = Object.entries(storesMapped || {});
                        if (entries.length > 0) {
                            const v = entries[0][1];
                            directVendor = String((v && typeof v === 'object') ? (v.remote_store_id || v.remoteId || v.remote_store || v) : v);
                        }
                    }

                    if (directVendor) {
                        vendorId = directVendor;
                    } else {
                        toast.error('vendorId not existing');
                        setIsGlovoSistersSubmitting(false);
                        return;
                    }
                    }
                }

                // For Glovo Sisters we prefer to read the vendorId directly from the
                // platform mapping `remote_store_id`. Try several fallbacks instead
                // of failing when the internal Store can't be resolved.
                // Attempt to locate an internal store if possible (best-effort)
                const internalStore = (Array.isArray(stores) ? stores : []).find(s => {
                    const candidates = [s._id, s.value, s.id, s.rawId];
                    return candidates.some(c => c && String(c) === String(storeIdCandidate));
                }) || null;

                // Try to resolve a mapping entry. Accept several shapes and fallbacks:
                // - Array of mappings [{ store_id, remote_store_id }, ...]
                // - Object map { <localId>: <remoteId> } or { <localId>: { remote_store_id } }
                let match = null;
                if (Array.isArray(storesMapped)) {
                    // Prefer an exact match by store_id (using candidate or internal store id),
                    // otherwise try matching remote_store_id to the candidate, otherwise fall
                    // back to the single entry when there's only one.
                    match = storesMapped.find(m => {
                        const sid = String(m.store_id || m.storeId || '');
                        const rid = String(m.remote_store_id || m.remoteId || '');
                        if (storeIdCandidate && sid && String(sid) === String(storeIdCandidate)) return true;
                        if (internalStore && sid && String(sid) === String(internalStore._id || internalStore.id || internalStore.value)) return true;
                        if (storeIdCandidate && rid && String(rid) === String(storeIdCandidate)) return true;
                        return false;
                    }) || null;
                    if (!match && storesMapped.length === 1) match = storesMapped[0];
                } else if (typeof storesMapped === 'object') {
                    // object shape: { localId: remoteId } or { localId: { remote_store_id } }
                    if (storeIdCandidate && storesMapped[storeIdCandidate]) {
                        const v = storesMapped[storeIdCandidate];
                        match = (v && typeof v === 'object') ? { store_id: storeIdCandidate, remote_store_id: v.remote_store_id || v } : { store_id: storeIdCandidate, remote_store_id: v };
                    } else {
                        // try to find an entry where the value contains remote_store_id
                        const entries = Object.entries(storesMapped || {});
                        const found = entries.find(([k, v]) => (v && typeof v === 'object' && (v.remote_store_id || v.remoteId)) || (String(v) === String(storeIdCandidate)));
                        if (found) {
                            const [k, v] = found;
                            match = (v && typeof v === 'object') ? { store_id: k, remote_store_id: v.remote_store_id || v.remoteId || v } : { store_id: k, remote_store_id: v };
                        } else if (entries.length === 1) {
                            const [k, v] = entries[0];
                            match = (v && typeof v === 'object') ? { store_id: k, remote_store_id: v.remote_store_id || v.remoteId || v } : { store_id: k, remote_store_id: v };
                        }
                    }
                }
console.log('[GlovoSisters] resolved platform mapping entry:', match);
                if (!match || !match.remote_store_id) {
                    toast.error('vendorId not existing');
                    setIsGlovoSistersSubmitting(false);
                    return;
                }

                vendorId = String(match.remote_store_id);
            } catch (err) {
                console.warn('[GlovoSisters] error extracting vendorId from platform mapping', err);
                toast.error('vendorId not existing (error parsing mapping)');
                setIsGlovoSistersSubmitting(false);
                return;
            }

            // Ensure vendorId is resolved from the platform referenced by plateformId
            try {
                const resolved = (Array.isArray(aggregatedPlatforms) ? aggregatedPlatforms.find(p => String(normalizeId(p._id || p.id)) === String(plateformId)) : null) || platformToUse;
                if (resolved) {
                    const typeKey = normalizePlatformKey(resolved?.type || '');
                    const nameKey = normalizePlatformKey(resolved?.name || '');
                    if (typeKey === 'glovo_sisters' || nameKey.includes('glovo_sis')) {
                        const add = resolved?.additionnal_data || resolved?.additional_data || {};
                        const portal = add.portal || add.Portal || null;
                        const storesMapped = portal?.stores_maped || portal?.stores_mapped || portal?.storesMapped || portal?.stores_map || add.stores_maped || add.stores_mapped || add.stores_map || null;
                        if (storesMapped) {
                            if (storesMapped && typeof storesMapped === 'object' && !Array.isArray(storesMapped) && (storesMapped.remote_store_id || storesMapped.remoteId || storesMapped.remote_store)) {
                                vendorId = String(storesMapped.remote_store_id || storesMapped.remoteId || storesMapped.remote_store);
                            } else if (Array.isArray(storesMapped) && storesMapped.length > 0) {
                                const e = storesMapped[0] || {};
                                vendorId = String(e.remote_store_id || e.remoteId || e.remote_store || e.remote || e);
                            } else if (storesMapped && typeof storesMapped === 'object') {
                                const entries = Object.entries(storesMapped || {});
                                if (entries.length > 0) {
                                    const v = entries[0][1];
                                    vendorId = String((v && typeof v === 'object') ? (v.remote_store_id || v.remoteId || v.remote_store || v) : v);
                                }
                            }
                        }
                    }
                }
            } catch (e) {
                console.warn('[GlovoSisters] vendorId override error', e);
            }

            const payload = {
                plateformId: plateformId,
                vendorId: vendorId,
                catalogId: selectedGlovoSistersCatalogId,
                categoryId: selectedGlovoSistersCategoryId,
                description: glovoSistersDescriptionDraft,
                productOptionIds: addonRemoteIds,
                price: glovoSistersParsedPrice,
                globalEntityId: 'GV_MA'
            };

            console.log('[GlovoSisters] sending payload:', payload);

            const response = await fetch(`${process.env.REACT_APP_PEGASUS_API_URL}/glovo-portal/product/add/${product._id}`, {
                method: 'POST',
                headers: {
                    ...header().headers,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (data.success) {
                saveRecentPlatformSelection('glovo_sisters', {
                    store: selectedCatalog?.storeId ? {
                        value: normalizeId(selectedCatalog.storeId),
                        label: selectedCatalog.storeName || selectedCatalog.label,
                        catalogId: selectedCatalog.value,
                    } : null,
                    catalog: selectedCatalog ? {
                        value: selectedCatalog.value,
                        label: selectedCatalog.label,
                        storeId: selectedCatalog.storeId,
                    } : null,
                    category: selectedGlovoSistersCategoryId ? {
                        value: selectedGlovoSistersCategoryId,
                        label: glovoSistersCategoryOptions.find((opt) => opt.value === selectedGlovoSistersCategoryId)?.label || selectedGlovoSistersCategoryId,
                        catalogId: selectedGlovoSistersCatalogId,
                    } : null,
                    addons: selectedGlovoSistersAddonsValue.map((addon) => ({
                        value: addon.value,
                        label: addon.label,
                    })),
                });

                const checkApproval = async (attempt = 1) => {
                    if (attempt > 5) {
                        toast.warning('Produit publié sur Glovo Sisters, mais la vérification a échoué après 5 tentatives.');
                        onClose();
                        return;
                    }

                    try {
                        if (attempt > 1) {
                            await new Promise(resolve => setTimeout(resolve, 10000));
                        } else {
                            await new Promise(resolve => setTimeout(resolve, 2000));
                        }

                        const checkResponse = await fetch(`${process.env.REACT_APP_PEGASUS_API_URL}/glovo-portal/commands/check`, {
                            method: 'POST',
                            headers: {
                                ...header().headers,
                                'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({
                                plateformId: plateformId || data.plateformId,
                                vendorId: data.vendorId,
                                commandId: data.command?.commandId,
                                globalEntityId: 'GV_MA',
                                product_id: data.productId
                            })
                        });

                        const checkData = await checkResponse.json();

                        console.log(`[Glovo Sisters Check] Attempt ${attempt}:`, checkData);

                        if (checkData.success && checkData.command?.status === 'APPROVED') {
                            toast.success('Produit publié et approuvé sur Glovo Sisters avec succès!');
                            const updatedProduct = await fetchUpdatedProduct(product._id);
                            if (updatedProduct && onProductUpdated) onProductUpdated(updatedProduct);
                            onClose();
                            return;
                        }

                        if (checkData.command?.status === 'REJECTED') {
                            toast.error(`Produit rejeté par Glovo Sisters: ${checkData.message || 'Raison inconnue'}`);
                            onClose();
                            return;
                        }

                        await checkApproval(attempt + 1);

                    } catch (error) {
                        console.error(`Error checking approval (attempt ${attempt}):`, error);
                        if (attempt < 5) {
                            await checkApproval(attempt + 1);
                        } else {
                            toast.warning('Produit publié sur Glovo Sisters, mais impossible de vérifier l\'approbation.');
                            onClose();
                        }
                    }
                };

                toast.success('Produit envoyé à Glovo Sisters. Vérification en arrière-plan.');
                onClose();
                checkApproval().catch((error) => {
                    console.error('Background Glovo Sisters approval check failed:', error);
                });
            } else {
                toast.error(`Erreur: ${data.error || 'Échec de publication'}`);
            }
        } catch (error) {
            console.error('Error publishing to Glovo Sisters:', error);
            toast.error('Erreur lors de la publication sur Glovo Sisters');
        } finally {
            setIsGlovoSistersSubmitting(false);
        }
    };
    
    const resolveImageUrl = (path) => {
        if (!path) return null;
        const normalized = `${path}`.replace(/^\//, "");
        if (normalized.startsWith("http")) {
            return normalized;
        }
        return `${process.env.REACT_APP_DATA_PIC_PATH}/${normalized}`;
    };

    return (
        <>
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="md" 
            fullWidth
            keepMounted={false}
            disableEnforceFocus
            disableAutoFocus
            disableRestoreFocus
            sx={{ zIndex: 1300 }}
            BackdropProps={{ sx: { zIndex: 1299 } }}
            PaperProps={{ sx: { pointerEvents: 'auto', zIndex: 1300, position: 'relative' } }}
        >
            <DialogTitle>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    {product?.url_product && (
                        <img 
                            src={resolveImageUrl(product.url_product)} 
                            alt={product?.name}
                            style={{ 
                                width: '80px', 
                                height: '80px', 
                                objectFit: 'cover',
                                borderRadius: '8px',
                                border: '1px solid #ddd'
                            }} 
                        />
                    )}
                    <div>
                        <div style={{ fontSize: '1.25rem', fontWeight: 500 }}>
                            Publier sur les Plateformes
                        </div>
                        <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '4px' }}>
                            {product?.name}
                        </div>
                    </div>
                </div>
            </DialogTitle>
            <DialogContent>
                <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
                    <Tab 
                        label={
                            <Badge color={hasAimer ? "success" : "default"} variant="dot">
                                Aimer
                            </Badge>
                        } 
                    />
                    <Tab 
                        label={
                            <Badge color={hasKooul ? "success" : "default"} variant="dot">
                                Kooul
                            </Badge>
                        } 
                    />
                    <Tab 
                        label={
                            <Badge color={hasGlovo ? "success" : "default"} variant="dot">
                                Glovo
                            </Badge>
                        } 
                    />
                        <Tab 
                            label={
                                <Badge color={hasGlovoSisters ? "success" : "default"} variant="dot">
                                    Glovo Sis
                                </Badge>
                            }
                        />
                    <Tab 
                        label={
                            <Badge color={hasSite ? "success" : "default"} variant="dot">
                                Site Web
                            </Badge>
                        } 
                    />
                    <Tab
                        label={
                            <Badge color={hasMarjane ? "success" : "default"} variant="dot">
                                Marjane
                            </Badge>
                        }
                    />
                    <Tab 
                        label={
                            <Badge color={hasInstagram ? "success" : "default"} variant="dot">
                                Instagram
                            </Badge>
                        } 
                    />
                    <Tab
                        label={
                            <Badge color={hasDolibarr ? "success" : "default"} variant="dot">
                                Compta
                            </Badge>
                        }
                    />
                    <Tab label={`Reset (${resetPlateformRows.length})`} />
                </Tabs>

                {/* Aimer Tab */}
                <TabPanel value={activeTab} index={0}>
                    {hasAimer ? (
                        <div className="alert alert-success">
                            <i className="fa-solid fa-check-circle me-2"></i>
                            Produit déjà publié sur Aimer
                        </div>
                    ) : (
                        <div>
                            <div className="mb-3">
                                <label className="form-label">Catégories Aimer</label>
                                <Autocomplete
                                    multiple
                                    disableCloseOnSelect
                                    disablePortal
                                    options={aimerCategoryOptions}
                                    value={selectedAimerCategoriesValue}
                                    getOptionLabel={(opt) => opt?.label || ''}
                                    renderOption={renderOptionWithKey}
                                    isOptionEqualToValue={(opt, val) => normalizeId(opt?.value) === normalizeId(val?.value)}
                                    onChange={(_, newValue) => {
                                        const ids = (newValue || []).map(v => normalizeId(v.value));
                                        setLocalAimerCategoryIds(ids);
                                    }}
                                    renderInput={(params) => (
                                        <TextField {...params} placeholder="Sélectionner des catégories" size="small" />
                                    )}
                                    disabled={isAimerSubmitting}
                                />
                            </div>
                            <button
                                className="btn btn-primary w-100"
                                onClick={handleAimerSubmit}
                                disabled={isAimerSubmitting || !localAimerCategoryIds?.length}
                            >
                                {isAimerSubmitting ? 'Publication...' : 'Publier sur Aimer'}
                            </button>
                        </div>
                    )}
                </TabPanel>

                {/* Kooul Tab */}
                <TabPanel value={activeTab} index={1}>
                    {hasKooul ? (
                        <div className="alert alert-success">
                            <i className="fa-solid fa-check-circle me-2"></i>
                            Produit déjà publié sur Kooul
                        </div>
                    ) : (
                        <div>
                            <div className="mb-3">
                                <label className="form-label">Plateforme Kooul</label>
                                <Autocomplete
                                    disablePortal
                                    options={kooulPlateformOptions}
                                    value={selectedKooulPlateformValue}
                                    getOptionLabel={(opt) => opt?.label || ''}
                                    renderOption={renderOptionWithKey}
                                    isOptionEqualToValue={(opt, val) => normalizeId(opt?.value) === normalizeId(val?.value)}
                                    onChange={(_, newValue) => {
                                        if (newValue) {
                                            const id = normalizeId(newValue.value);
                                            setLocalKooulPlateformId(id);
                                            setLocalKooulPlateform(newValue);
                                        } else {
                                            setLocalKooulPlateformId(null);
                                            setLocalKooulPlateform(null);
                                        }
                                        setLocalKooulCategoryIds([]);
                                        setExpandedKooulNodes(new Set());
                                        setSelectedKooulLeafCategory(null);
                                    }}
                                    renderInput={(params) => (
                                        <TextField {...params} placeholder="Sélectionner une plateforme" size="small" />
                                    )}
                                    disabled={isKooulSubmitting}
                                />
                            </div>
                            {localKooulPlateformId && (
                                <div className="mb-3">
                                    <label className="form-label">
                                        Catégories
                                        <small className="text-muted ms-2">
                                            (Naviguez dans l'arborescence et sélectionnez une catégorie finale)
                                        </small>
                                    </label>
                                    
                                    {/* Display selected category path */}
                                    {selectedKooulLeafCategory && (
                                        <div className="mb-2 p-2 bg-light rounded">
                                            <div className="d-flex align-items-center">
                                                <CheckCircleIcon sx={{ color: 'success.main', mr: 1, fontSize: 18 }} />
                                                <small className="text-muted me-2">Sélection:</small>
                                                <div className="d-flex align-items-center flex-wrap gap-1">
                                                    {getCategoryPath(selectedKooulLeafCategory.id).map((cat, idx, arr) => (
                                                        <React.Fragment key={cat.id}>
                                                            <Chip 
                                                                label={cat.name} 
                                                                size="small" 
                                                                color={idx === arr.length - 1 ? "primary" : "default"}
                                                                sx={{ fontSize: 12 }}
                                                            />
                                                            {idx < arr.length - 1 && (
                                                                <ChevronRightIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                            )}
                                                        </React.Fragment>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Category tree */}
                                    <Box 
                                        sx={{ 
                                            border: '1px solid #e0e0e0', 
                                            borderRadius: 1, 
                                            maxHeight: 400, 
                                            overflowY: 'auto',
                                            bgcolor: '#fafafa'
                                        }}
                                    >
                                        {kooulCategoryTree.length > 0 ? (
                                            <List dense>
                                                {renderKooulCategoryTree(kooulCategoryTree)}
                                            </List>
                                        ) : (
                                            <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                                                <i className="fa-solid fa-folder-open fa-2x mb-2"></i>
                                                <p className="mb-0">Aucune catégorie disponible</p>
                                            </Box>
                                        )}
                                    </Box>
                                </div>
                            )}
                            <button
                                className="btn btn-primary w-100"
                                onClick={handleKooulSubmit}
                                disabled={isKooulSubmitting || !localKooulPlateformId || !selectedKooulLeafCategory}
                            >
                                {isKooulSubmitting ? 'Publication...' : 'Publier sur Kooul'}
                            </button>
                        </div>
                    )}
                </TabPanel>

                {/* Glovo Tab */}
                <TabPanel value={activeTab} index={2}>
                    {hasGlovo ? (
                        <div className="alert alert-success">
                            <i className="fa-solid fa-check-circle me-2"></i>
                            Produit déjà publié sur Glovo
                        </div>
                    ) : glovoCatalogOptions.length === 0 ? (
                        <div>
                            <div className="alert alert-info mb-3">
                                <i className="fa-solid fa-info-circle me-2"></i>
                                Tous les catalogues disponibles sont déjà configurés pour ce produit.
                            </div>
                            
                            {/* Show existing roadmap entries */}
                            {product?.plateforms && Array.isArray(product.plateforms) && (
                                <div className="card">
                                    <div className="card-header bg-primary text-white">
                                        <i className="fa-solid fa-map me-2"></i>
                                        Catalogues Configurés
                                    </div>
                                    <div className="card-body">
                                        {product.plateforms.map((platform, platformIdx) => {
                                            if (!Array.isArray(platform.roadmap) || platform.roadmap.length === 0) return null;
                                            
                                            const glovoRoadmaps = platform.roadmap.filter(r => r.type === 'glovo');
                                            if (glovoRoadmaps.length === 0) return null;
                                            
                                            return glovoRoadmaps.map((roadmap, roadmapIdx) => {
                                                const catalogIds = roadmap.data?.catalogIds || [];
                                                const isEnabled = roadmap.data?.availability?.available || false;
                                                const createdDate = roadmap.createdAt?.$date 
                                                    ? new Date(roadmap.createdAt.$date).toLocaleString('fr-FR')
                                                    : roadmap.createdAt
                                                    ? new Date(roadmap.createdAt).toLocaleString('fr-FR')
                                                    : 'N/A';
                                                const updatedDate = roadmap.updatedAt?.$date 
                                                    ? new Date(roadmap.updatedAt.$date).toLocaleString('fr-FR')
                                                    : roadmap.updatedAt
                                                    ? new Date(roadmap.updatedAt).toLocaleString('fr-FR')
                                                    : 'N/A';
                                                
                                                return (
                                                    <div key={`${platformIdx}-${roadmapIdx}`} className="mb-3 border-bottom pb-3">
                                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                                            <div>
                                                                <h6 className="mb-1">
                                                                    <i className="fa-solid fa-store me-1"></i>
                                                                    VendorId: <code>{roadmap.vendorId}</code>
                                                                </h6>
                                                                <small className="text-muted">
                                                                    Remote ID: <code>{roadmap.remote_id}</code>
                                                                </small>
                                                            </div>
                                                            <span className={`badge ${isEnabled ? 'bg-success' : 'bg-danger'}`}>
                                                                {isEnabled ? (
                                                                    <>
                                                                        <i className="fa-solid fa-check me-1"></i>
                                                                        Activé
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <i className="fa-solid fa-times me-1"></i>
                                                                        Désactivé
                                                                    </>
                                                                )}
                                                            </span>
                                                        </div>
                                                        
                                                        {catalogIds.length > 0 && (
                                                            <div className="mb-2">
                                                                <small className="text-muted d-block mb-1">Catalogues:</small>
                                                                {catalogIds.map((catId, catIdx) => (
                                                                    <span key={catIdx} className="badge bg-secondary me-1">
                                                                        {catId}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                        
                                                        {roadmap.data?.category && (
                                                            <div className="mb-2">
                                                                <small className="text-muted">
                                                                    Catégorie: <strong>{roadmap.data.category.name}</strong> ({roadmap.data.category.id})
                                                                </small>
                                                            </div>
                                                        )}
                                                        
                                                        <div className="small text-muted">
                                                            <div>
                                                                <i className="fa-solid fa-calendar-plus me-1"></i>
                                                                Créé: {createdDate}
                                                            </div>
                                                            <div>
                                                                <i className="fa-solid fa-calendar-check me-1"></i>
                                                                Mis à jour: {updatedDate}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            });
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div>
                            {renderQuickPickBadges('Magasins les plus utilises', glovoRecentStoreBadges, selectedGlovoCatalogId, (item) => {
                                const catalogId = item?.option?.value || null;
                                setSelectedGlovoCatalogId(catalogId);
                                setSelectedGlovoCategoryId(null);
                            })}
                            {renderQuickPickBadges('Catalogues les plus utilises', glovoRecentCatalogBadges, selectedGlovoCatalogId, (item) => {
                                const catalogId = item?.option?.value || null;
                                setSelectedGlovoCatalogId(catalogId);
                                setSelectedGlovoCategoryId(null);
                            })}
                            <div className="mb-3">
                                <label className="form-label h5">
                                    <i className="fa-solid fa-box me-2"></i>
                                    Sélectionner un Catalogue
                                </label>
                                <Autocomplete
                                    options={glovoCatalogOptions}
                                    value={glovoCatalogOptions.find(opt => opt.value === selectedGlovoCatalogId) || null}
                                    onChange={(event, newValue) => {
                                        const catalogId = newValue?.value || null;
                                        setSelectedGlovoCatalogId(catalogId);
                                        setSelectedGlovoCategoryId(null);
                                        console.log('Selected Glovo Catalog:', catalogId);
                                    }}
                                    getOptionLabel={(option) => option.label || ''}
                                    renderInput={(params) => (
                                        <TextField {...params} placeholder="Choisir un catalogue" />
                                    )}
                                    renderOption={renderOptionWithKey}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    filterOptions={(options, state) => {
                                        const inputValue = state.inputValue.toLowerCase();
                                        if (!inputValue) return options;
                                        return options.filter(option => 
                                            option.label.toLowerCase().includes(inputValue) ||
                                            option.value.toLowerCase().includes(inputValue) ||
                                            (option.storeName && option.storeName.toLowerCase().includes(inputValue))
                                        );
                                    }}
                                />
                            </div>
                            
                            {selectedGlovoCatalogId && (
                                <>
                                    {renderQuickPickBadges('Categories les plus utilisees', glovoRecentCategoryBadges, selectedGlovoCategoryId, (item) => {
                                    setSelectedGlovoCategoryId(item?.option?.value || null);
                                })}
                            <div className="mb-3">
                                    <label className="form-label h5">
                                        <i className="fa-solid fa-folder me-2"></i>
                                        Sélectionner une Catégorie
                                    </label>
                                    <Autocomplete
                                        options={glovoCategoryOptions}
                                        value={glovoCategoryOptions.find(opt => opt.value === selectedGlovoCategoryId) || null}
                                        onChange={(event, newValue) => {
                                            const categoryId = newValue?.value || null;
                                            setSelectedGlovoCategoryId(categoryId);
                                            console.log('Selected Glovo Category:', {
                                                categoryId,
                                                categoryLabel: newValue?.label,
                                                catalogId: selectedGlovoCatalogId
                                            });
                                        }}
                                        getOptionLabel={(option) => option.label || ''}
                                        renderInput={(params) => (
                                            <TextField {...params} placeholder="Choisir une catégorie" />
                                        )}
                                        renderOption={renderOptionWithKey}
                                        isOptionEqualToValue={(option, value) => option.value === value.value}
                                        filterOptions={(options, state) => {
                                            const inputValue = state.inputValue.toLowerCase();
                                            if (!inputValue) return options;
                                            return options.filter(option => 
                                                option.label.toLowerCase().includes(inputValue) ||
                                                option.value.toLowerCase().includes(inputValue)
                                            );
                                        }}
                                    />
                                </div>
                                </>
                            )}
                            
                            {selectedGlovoCatalogId && (
                                <>
                                    {renderQuickPickBadges('Addons les plus utilises', glovoRecentAddonBadges, selectedGlovoAddonIds, (item) => {
                                    const addonId = normalizeId(item?.option?.value || item?.value);
                                    setSelectedGlovoAddonIds((prev) => prev.includes(addonId) ? prev.filter((value) => value !== addonId) : [...prev, addonId]);
                                })}
                            <div className="mb-3">
                                    <label className="form-label h5">
                                        <i className="fa-solid fa-puzzle-piece me-2"></i>
                                        Addons / Options (Optionnel)
                                    </label>
                                    <Autocomplete
                                        multiple
                                        disableCloseOnSelect
                                        options={glovoAddonOptions}
                                        value={selectedGlovoAddonsValue}
                                        onChange={(event, newValue) => {
                                            const addonIds = newValue.map(v => v.value);
                                            setSelectedGlovoAddonIds(addonIds);
                                            console.log('Selected Glovo Addons:', {
                                                addonIds,
                                                remoteIds: newValue.map(v => v.remoteId)
                                            });
                                        }}
                                        getOptionLabel={(option) => option.label || ''}
                                        renderInput={(params) => (
                                            <TextField {...params} placeholder="Sélectionner des addons (optionnel)" />
                                        )}
                                        renderOption={(props, option) => (
                                            <li {...props} key={option.value}>
                                                <div style={{ width: '100%' }}>
                                                    <div style={{ fontWeight: 500 }}>{option.label}</div>
                                                    {option.optionNames && option.optionNames.length > 0 && (
                                                        <div style={{ fontSize: '0.85em', marginTop: '4px' }}>
                                                            {option.optionNames.map((optName, idx) => (
                                                                <span 
                                                                    key={idx} 
                                                                    className="badge bg-secondary me-1 mb-1"
                                                                    style={{ fontSize: '0.75em' }}
                                                                >
                                                                    {optName}
                                                                </span>
                                                            ))}
                                                            {option.fullAddon?.options?.length > 5 && (
                                                                <span className="text-muted" style={{ fontSize: '0.75em' }}>
                                                                    +{option.fullAddon.options.length - 5} more
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </li>
                                        )}
                                        renderTags={(value, getTagProps) =>
                                            value.map((option, index) => (
                                                <span
                                                    key={option.value}
                                                    {...getTagProps({ index })}
                                                    className="badge bg-info me-1 mb-1"
                                                    style={{ fontSize: '0.9em', padding: '0.4em 0.6em' }}
                                                >
                                                    {option.label}
                                                    <i 
                                                        className="fa-solid fa-times ms-2" 
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => {
                                                            const newIds = selectedGlovoAddonIds.filter(id => id !== option.value);
                                                            setSelectedGlovoAddonIds(newIds);
                                                        }}
                                                    ></i>
                                                </span>
                                            ))
                                        }
                                        isOptionEqualToValue={(option, value) => option.value === value.value}
                                        filterOptions={(options, state) => {
                                            const inputValue = state.inputValue.toLowerCase();
                                            if (!inputValue) return options;
                                            return options.filter(option => 
                                                option.label.toLowerCase().includes(inputValue) ||
                                                option.optionNames.some(name => name.toLowerCase().includes(inputValue))
                                            );
                                        }}
                                    />
                                </div>
                                </>
                            )}
                            
                            {selectedGlovoCatalogId && (
                                <div className="mb-3">
                                    <label className="form-label h5"><i className="fa-solid fa-money-bill-wave me-2"></i>Prix a publier</label>
                                    <TextField fullWidth type="number" value={glovoPriceDraft} onChange={(e) => setGlovoPriceDraft(e.target.value)} inputProps={{ min: 0, step: '0.01' }} placeholder="Prix du produit sur Glovo" />
                                    {!isGlovoPriceValid && glovoPriceDraft !== '' && <div className="alert alert-warning mt-2 mb-0">Entrez un prix valide pour publier sur Glovo.</div>}
                                </div>
                            )}
                            <div className="mb-3">
                                <label className="form-label h5">
                                    <i className="fa-solid fa-align-left me-2"></i>
                                    Description du produit
                                    <small className="text-muted ms-2">
                                        (Min: 50 caractères, Max: 200 caractères)
                                    </small>
                                </label>
                                <TextField
                                    inputRef={textareaRef}
                                    fullWidth
                                    multiline
                                    minRows={6}
                                    value={glovoDescriptionDraft}
                                    onChange={handleGlovoDescriptionChange}
                                    onFocus={(e) => {
                                        console.log('[Glovo TextField] onFocus', {
                                            value: e.target.value,
                                            activeElement: document.activeElement
                                        });
                                    }}
                                    onBlur={(e) => {
                                        console.log('[Glovo TextField] onBlur', {
                                            value: e.target.value,
                                            relatedTarget: e.relatedTarget
                                        });
                                    }}
                                    onClick={(e) => {
                                        console.log('[Glovo TextField] onClick', {
                                            target: e.target,
                                            currentTarget: e.currentTarget
                                        });
                                    }}
                                    onMouseDown={(e) => {
                                        console.log('[Glovo TextField] onMouseDown', {
                                            button: e.button,
                                            target: e.target
                                        });
                                    }}
                                    placeholder="Entrez une description pour le produit..."
                                    autoComplete="off"
                                />
                                <div className="small mt-2">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span className={
                                            glovoDescriptionLength < 50 
                                                ? 'text-danger' 
                                                : glovoDescriptionLength > 200 
                                                ? 'text-danger' 
                                                : 'text-success'
                                        }>
                                            <i className={`fa-solid ${
                                                glovoDescriptionLength >= 50 && glovoDescriptionLength <= 200 
                                                    ? 'fa-check-circle' 
                                                    : 'fa-exclamation-circle'
                                            } me-1`}></i>
                                            {glovoDescriptionLength}/200 caractères
                                        </span>
                                        {glovoDescriptionLength < 50 && glovoDescriptionLength > 0 && (
                                            <span className="text-danger">
                                                Encore {50 - glovoDescriptionLength} caractères requis
                                            </span>
                                        )}
                                    </div>
                                    {!isGlovoDescriptionCompliant && glovoDescriptionLength > 0 && (
                                        <div className="alert alert-warning mt-2 mb-0">
                                            <i className="fa-solid fa-exclamation-triangle me-2"></i>
                                            {glovoDescriptionLength < 50 
                                                ? `La description doit contenir au moins 50 caractères (actuellement ${glovoDescriptionLength}).`
                                                : `La description ne doit pas dépasser 200 caractères (actuellement ${glovoDescriptionLength}).`
                                            }
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            {selectedGlovoCatalogId && selectedGlovoCategoryId && (
                                <div className="d-grid gap-2">
                                    <button className="btn btn-primary w-100" onClick={handleGlovoSubmit} disabled={isGlovoSubmitting || isBulkGlovoSubmitting || !isGlovoDescriptionCompliant || !isGlovoPriceValid}>
                                        {isGlovoSubmitting ? (<><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Publication...</>) : (<><i className="fa-solid fa-plus-circle me-2"></i>Creer le produit sur Glovo</>)}
                                    </button>
                                    <div className="card border-primary-subtle">
                                        <div className="card-body py-3">
                                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                                <div>
                                                    <div className="fw-semibold">
                                                        <i className="fa-solid fa-layer-group me-2"></i>
                                                        Actions Glovo en lot
                                                    </div>
                                                    <div className="small text-muted">
                                                        {bulkGlovoCandidates.length > 0
                                                            ? `${bulkGlovoCandidates.length} produit(s) selectionne(s) eligible(s) pour la publication en lot.`
                                                            : selectedProductIdsSet.size > 0
                                                            ? 'Aucun produit selectionne n’est eligible pour l’action en lot.'
                                                            : 'Selectionnez des produits pour activer la publication Glovo en lot.'}
                                                    </div>
                                                </div>
                                                {bulkGlovoCandidates.length > 0 && (
                                                    <button type="button" className="btn btn-outline-primary" onClick={handleBulkGlovoSubmit} disabled={isGlovoSubmitting || isBulkGlovoSubmitting || !isGlovoDescriptionCompliant || !isGlovoPriceValid}>
                                                        {isBulkGlovoSubmitting ? (<><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Publication en lot...</>) : (<><i className="fa-solid fa-layer-group me-2"></i>Publier la selection ({bulkGlovoCandidates.length})</>)}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {bulkGlovoQueue.length > 0 && (
                                <div className="card mt-3">
                                    <div className="card-header">File d&apos;attente Glovo</div>
                                    <div className="card-body">
                                        {bulkGlovoQueue.map((item) => (
                                            <div key={item.productId} className="d-flex justify-content-between align-items-center mb-2">
                                                <span>{item.productName}</span>
                                                <span className={'badge ' + (item.status === 'done' ? 'bg-success' : item.status === 'error' ? 'bg-danger' : item.status === 'in-progress' ? 'bg-warning text-dark' : 'bg-secondary')}>{item.status === 'done' ? 'Publie' : item.status === 'error' ? 'Erreur' : item.status === 'in-progress' ? 'En cours' : 'En attente'}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                                                        {selectedGlovoCatalogId && selectedGlovoCategoryId && !isGlovoDescriptionCompliant && (
                                <div className="alert alert-warning mt-3">
                                    <i className="fa-solid fa-exclamation-triangle me-2"></i>
                                    La description doit contenir au moins 50 caractères et ne pas dépasser 200 caractères.
                                </div>
                            )}
                        </div>
                    )}
                </TabPanel>

                {/* Glovo Sisters Tab */}
                <TabPanel value={activeTab} index={3}>
                    {hasGlovoSisters ? (
                        <div className="alert alert-success">
                            <i className="fa-solid fa-check-circle me-2"></i>
                            Produit déjà publié sur Glovo Sisters
                        </div>
                    ) : glovoSistersCatalogOptions.length === 0 ? (
                        <div className="alert alert-info">Aucun catalogue Glovo Sisters disponible ou déjà configuré pour ce produit.</div>
                    ) : (
                        <div>
                            {renderQuickPickBadges('Magasins les plus utilises', glovoSistersRecentStoreBadges, selectedGlovoSistersCatalogId, (item) => {
                                const catalogId = item?.option?.value || null;
                                setSelectedGlovoSistersCatalogId(catalogId);
                                setSelectedGlovoSistersCategoryId(null);
                            })}
                            {renderQuickPickBadges('Catalogues les plus utilises', glovoSistersRecentCatalogBadges, selectedGlovoSistersCatalogId, (item) => {
                                const catalogId = item?.option?.value || null;
                                setSelectedGlovoSistersCatalogId(catalogId);
                                setSelectedGlovoSistersCategoryId(null);
                            })}
                            <div className="mb-3">
                                <label className="form-label h5">
                                    <i className="fa-solid fa-box me-2"></i>
                                    Sélectionner un Catalogue (Glovo Sisters)
                                </label>
                                <Autocomplete
                                    options={glovoSistersCatalogOptions}
                                    value={glovoSistersCatalogOptions.find(opt => opt.value === selectedGlovoSistersCatalogId) || null}
                                    onChange={(event, newValue) => {
                                        const catalogId = newValue?.value || null;
                                        setSelectedGlovoSistersCatalogId(catalogId);
                                        setSelectedGlovoSistersCategoryId(null);
                                    }}
                                    getOptionLabel={(option) => option.label || ''}
                                    renderInput={(params) => (
                                        <TextField {...params} placeholder="Choisir un catalogue" />
                                    )}
                                    renderOption={renderOptionWithKey}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                />
                            </div>

                            {selectedGlovoSistersCatalogId && (
                                <>
                                    {renderQuickPickBadges('Categories les plus utilisees', glovoSistersRecentCategoryBadges, selectedGlovoSistersCategoryId, (item) => {
                                    setSelectedGlovoSistersCategoryId(item?.option?.value || null);
                                })}
                            <div className="mb-3">
                                    <label className="form-label h5">
                                        <i className="fa-solid fa-folder me-2"></i>
                                        Sélectionner une Catégorie
                                    </label>
                                    <Autocomplete
                                        options={glovoSistersCategoryOptions}
                                        value={glovoSistersCategoryOptions.find(opt => opt.value === selectedGlovoSistersCategoryId) || null}
                                        onChange={(event, newValue) => {
                                            const categoryId = newValue?.value || null;
                                            setSelectedGlovoSistersCategoryId(categoryId);
                                        }}
                                        getOptionLabel={(option) => option.label || ''}
                                        renderInput={(params) => (
                                            <TextField {...params} placeholder="Choisir une catégorie" />
                                        )}
                                        renderOption={renderOptionWithKey}
                                        isOptionEqualToValue={(option, value) => option.value === value.value}
                                    />
                                </div>
                                </>
                            )}

                            {selectedGlovoSistersCatalogId && (
                                <>
                                    {renderQuickPickBadges('Addons les plus utilises', glovoSistersRecentAddonBadges, selectedGlovoSistersAddonIds, (item) => {
                                    const addonId = normalizeId(item?.option?.value || item?.value);
                                    setSelectedGlovoSistersAddonIds((prev) => prev.includes(addonId) ? prev.filter((value) => value !== addonId) : [...prev, addonId]);
                                })}
                            <div className="mb-3">
                                    <label className="form-label h5">
                                        <i className="fa-solid fa-puzzle-piece me-2"></i>
                                        Addons / Options (Optionnel)
                                    </label>
                                    <Autocomplete
                                        multiple
                                        disableCloseOnSelect
                                        options={glovoSistersAddonOptions}
                                        value={selectedGlovoSistersAddonsValue}
                                        onChange={(event, newValue) => {
                                            const addonIds = newValue.map(v => v.value);
                                            setSelectedGlovoSistersAddonIds(addonIds);
                                        }}
                                        getOptionLabel={(option) => option.label || ''}
                                        renderInput={(params) => (
                                            <TextField {...params} placeholder="Sélectionner des addons (optionnel)" />
                                        )}
                                        renderOption={(props, option) => (
                                            <li {...props} key={option.value}>
                                                <div style={{ width: '100%' }}>
                                                    <div style={{ fontWeight: 500 }}>{option.label}</div>
                                                    {option.optionNames && option.optionNames.length > 0 && (
                                                        <div style={{ fontSize: '0.85em', marginTop: '4px' }}>
                                                            {option.optionNames.map((optName, idx) => (
                                                                <span 
                                                                    key={idx} 
                                                                    className="badge bg-secondary me-1 mb-1"
                                                                    style={{ fontSize: '0.75em' }}
                                                                >
                                                                    {optName}
                                                                </span>
                                                            ))}
                                                            {option.fullAddon?.options?.length > 5 && (
                                                                <span className="text-muted" style={{ fontSize: '0.75em' }}>
                                                                    +{option.fullAddon.options.length - 5} more
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </li>
                                        )}
                                        renderTags={(value, getTagProps) =>
                                            value.map((option, index) => (
                                                <span
                                                    key={option.value}
                                                    {...getTagProps({ index })}
                                                    className="badge bg-info me-1 mb-1"
                                                    style={{ fontSize: '0.9em', padding: '0.4em 0.6em' }}
                                                >
                                                    {option.label}
                                                </span>
                                            ))
                                        }
                                        isOptionEqualToValue={(option, value) => option.value === value.value}
                                    />
                                </div>
                                </>
                            )}

                            {selectedGlovoSistersCatalogId && (
                                <div className="mb-3">
                                    <label className="form-label h5"><i className="fa-solid fa-money-bill-wave me-2"></i>Prix a publier</label>
                                    <TextField fullWidth type="number" value={glovoSistersPriceDraft} onChange={(e) => setGlovoSistersPriceDraft(e.target.value)} inputProps={{ min: 0, step: '0.01' }} placeholder="Prix du produit sur Glovo Sisters" />
                                    {!isGlovoSistersPriceValid && glovoSistersPriceDraft !== '' && <div className="alert alert-warning mt-2 mb-0">Entrez un prix valide pour publier sur Glovo Sisters.</div>}
                                </div>
                            )}
                            <div className="mb-3">
                                <label className="form-label h5">
                                    <i className="fa-solid fa-align-left me-2"></i>
                                    Description du produit
                                    <small className="text-muted ms-2">
                                        (Min: 50 caractères, Max: 200 caractères)
                                    </small>
                                </label>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={6}
                                    value={glovoSistersDescriptionDraft}
                                    onChange={(e) => setGlovoSistersDescriptionDraft(e.target.value)}
                                    placeholder="Entrez une description pour le produit..."
                                    autoComplete="off"
                                />
                                <div className="small mt-2">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span className={
                                            glovoSistersDescriptionLength < 50 
                                                ? 'text-danger' 
                                                : glovoSistersDescriptionLength > 200 
                                                ? 'text-danger' 
                                                : 'text-success'
                                        }>
                                            <i className={`fa-solid ${
                                                glovoSistersDescriptionLength >= 50 && glovoSistersDescriptionLength <= 200 
                                                    ? 'fa-check-circle' 
                                                    : 'fa-exclamation-circle'
                                            } me-1`}></i>
                                            {glovoSistersDescriptionLength}/200 caractères
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {selectedGlovoSistersCatalogId && selectedGlovoSistersCategoryId && (
                                <button
                                    className="btn btn-primary w-100"
                                    onClick={handleGlovoSistersSubmit}
                                    disabled={isGlovoSistersSubmitting || isBulkGlovoSistersSubmitting || !isGlovoSistersDescriptionCompliant || !isGlovoSistersPriceValid}
                                >
                                    {isGlovoSistersSubmitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Publication...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa-solid fa-plus-circle me-2"></i>
                                            Créer le produit sur Glovo Sisters
                                        </>
                                    )}
                                </button>
                            )}

                            {selectedGlovoSistersCatalogId && selectedGlovoSistersCategoryId && (
                                <div className="card border-primary-subtle mt-2">
                                    <div className="card-body py-3">
                                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                            <div>
                                                <div className="fw-semibold">
                                                    <i className="fa-solid fa-layer-group me-2"></i>
                                                    Actions Glovo Sisters en lot
                                                </div>
                                                <div className="small text-muted">
                                                    {bulkGlovoSistersCandidates.length > 0
                                                        ? `${bulkGlovoSistersCandidates.length} produit(s) selectionne(s) eligible(s) pour la publication en lot.`
                                                        : selectedProductIdsSet.size > 0
                                                        ? "Aucun produit selectionne n'est eligible pour l'action en lot."
                                                        : 'Selectionnez des produits pour activer la publication Glovo Sisters en lot.'}
                                                </div>
                                            </div>
                                            {bulkGlovoSistersCandidates.length > 0 && (
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-primary"
                                                    onClick={handleBulkGlovoSistersSubmit}
                                                    disabled={isGlovoSistersSubmitting || isBulkGlovoSistersSubmitting || !selectedGlovoSistersCatalogId || !selectedGlovoSistersCategoryId}
                                                >
                                                    {isBulkGlovoSistersSubmitting ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                            Publication en lot...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="fa-solid fa-layer-group me-2"></i>
                                                            Publier la selection ({bulkGlovoSistersCandidates.length})
                                                        </>
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {bulkGlovoSistersQueue.length > 0 && (
                                <div className="card mt-3">
                                    <div className="card-header">File d&apos;attente Glovo Sisters</div>
                                    <div className="card-body">
                                        {bulkGlovoSistersQueue.map((item) => (
                                            <div key={item.productId} className="d-flex justify-content-between align-items-center mb-2">
                                                <span>{item.productName}</span>
                                                <span className={'badge ' + (item.status === 'done' ? 'bg-success' : item.status === 'error' ? 'bg-danger' : item.status === 'in-progress' ? 'bg-warning text-dark' : 'bg-secondary')}>
                                                    {item.status === 'done' ? 'Publie' : item.status === 'error' ? 'Erreur' : item.status === 'in-progress' ? 'En cours' : 'En attente'}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </TabPanel>

                {/* Site Web Tab */}
                <TabPanel value={activeTab} index={4}>
                    <div>
                        {hasSite && (
                            <div className="alert alert-info">
                                <i className="fa-solid fa-globe me-2"></i>
                                Produit deja publie sur le site. Vous pouvez mettre a jour les categories, le prix et le stock.
                            </div>
                        )}
                        <div className="d-flex justify-content-end mb-3">
                            <div className="form-check form-switch mb-0">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    id="siteStockStatusSwitch"
                                    checked={localSiteInStock}
                                    onChange={(e) => setLocalSiteInStock(e.target.checked)}
                                    disabled={isSiteSubmitting}
                                />
                                <label className="form-check-label" htmlFor="siteStockStatusSwitch">
                                    {localSiteInStock ? 'En stock' : 'Rupture de stock'}
                                </label>
                            </div>
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Categories</label>
                            <Autocomplete
                                multiple
                                disableCloseOnSelect
                                disablePortal
                                options={siteCategoryOptions}
                                value={selectedSiteCategoriesValue}
                                getOptionLabel={(opt) => opt?.label || ''}
                                renderOption={renderOptionWithKey}
                                isOptionEqualToValue={(opt, val) => normalizeId(opt?.value) === normalizeId(val?.value)}
                                onChange={(_, newValue) => {
                                    const selected = (newValue || []).map(v => v || {});
                                    const idsSet = new Set();
                                    selected.forEach(opt => {
                                        const id = normalizeId(opt.value);
                                        if (id) idsSet.add(id);
                                        const parentRaw = opt.parent;
                                        if (parentRaw && String(parentRaw) !== '0') {
                                            const parentId = normalizeId(parentRaw);
                                            if (parentId) idsSet.add(parentId);
                                        }
                                    });
                                    setLocalSiteCategoryIds(Array.from(idsSet));
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} placeholder="Selectionner des categories" size="small" />
                                )}
                                disabled={isSiteSubmitting}
                            />
                        </div>
                        {(
                            <div className="mb-3">
                                <label className="form-label" htmlFor="publish-site-fees">Frais de livraison à intégrer au prix</label>
                                <input 
                                    id="publish-site-fees"
                                    type="number" 
                                    className="form-control" 
                                    min={0} 
                                    step="0.01"
                                    value={localSiteFees} 
                                    onChange={handleSiteFeesChange} 
                                    disabled={isSiteSubmitting}
                                />
                            </div>
                        )}
                        <div className="mb-3">
                            <label className="form-label" htmlFor="publish-site-price">Prix du produit hors frais intégrés</label>
                            <input
                                id="publish-site-price"
                                type="number"
                                className="form-control"
                                min={0}
                                step="0.01"
                                value={localSitePrice}
                                onChange={handleSitePriceChange}
                                disabled={isSiteSubmitting}
                            />
                        </div>
                        {(
                            <div className='text-end mb-3'>
                                <div>{localSitePrice || 0} + {localSiteFees || 0} DHS</div>
                                <h2>Prix Sur Site : <span className="badge bg-warning">{Number.isFinite(siteTotalPrice) ? siteTotalPrice.toFixed(2) : '—'} DHS</span></h2>
                            </div>
                        )}
                        <div className="mb-3">
                            <label className="form-label" htmlFor="publish-site-vase">Vase sur la photo</label>
                            <select id="publish-site-vase" className="form-select" value={localSiteVase}
                                onChange={(event) => setLocalSiteVase(event.target.value)} disabled={isSiteSubmitting}>
                                <option value="unspecified">Non renseigné (aucun badge)</option>
                                <option value="included">Vase inclus</option>
                                <option value="not_included">Vase non inclus</option>
                            </select>
                        </div>
                        <div className="form-check mb-2">
                            <input id="publish-site-express" type="checkbox" className="form-check-input"
                                checked={localSiteExpress} onChange={(event) => setLocalSiteExpress(event.target.checked)} disabled={isSiteSubmitting} />
                            <label className="form-check-label" htmlFor="publish-site-express">Livraison express</label>
                        </div>
                        {localSiteExpress && (
                            <Autocomplete multiple options={deliveryZoneOptions}
                                value={localSiteExpressZones.map((zone) => ({ value: zone.code, label: zone.name || zone.code }))}
                                getOptionLabel={(option) => option.label || option.name || option.value || ''}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                onChange={(_, selected) => setLocalSiteExpressZones(selected.map((zone) => ({ code: zone.value, name: zone.label })))}
                                disabled={isSiteSubmitting}
                                renderInput={(params) => <TextField {...params} label="Zones express" size="small" helperText="Utilise les zones configurées pour la livraison express." />} />
                        )}
                        <div className="form-check mt-3 mb-3">
                            <input id="publish-site-morocco" type="checkbox" className="form-check-input"
                                checked={localSiteMorocco} onChange={(event) => setLocalSiteMorocco(event.target.checked)} disabled={isSiteSubmitting} />
                            <label className="form-check-label" htmlFor="publish-site-morocco">Livraison partout au Maroc</label>
                        </div>
                        {bulkSiteCandidates.length >= 2 && (
                            <div className="alert alert-secondary">
                                <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                                    <div>
                                        <strong>Publication bulk Site Web</strong>
                                        <div className="small text-muted">
                                            {bulkSiteCandidates.length} produit(s) sélectionné(s). Chaque produit garde son nom, description, images et code-barres. Son prix local est augmenté des frais saisis. Les catégories, le vase et les options de livraison choisis sont appliqués à tous.
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={handleBulkSiteSubmit}
                                        disabled={isSiteSubmitting || !localSiteCategoryIds?.length}
                                    >
                                        {isSiteSubmitting ? 'Publication bulk...' : `Publier les ${bulkSiteCandidates.length} sélectionnés`}
                                    </button>
                                </div>
                            </div>
                        )}
                        <button
                            className="btn btn-primary w-100"
                            onClick={handleSiteSubmit}
                            disabled={isSiteSubmitting || !localSiteCategoryIds?.length || localSitePrice === ''}
                        >
                            {isSiteSubmitting ? 'Publication...' : hasSite ? 'Mettre a jour le site' : 'Ajouter au Site'}
                        </button>
                    </div>
                </TabPanel>

                {/* MarjaneMall Tab */}
                <TabPanel value={activeTab} index={5}>
                    {hasMarjane ? (
                        <div className="alert alert-success">
                            <i className="fa-solid fa-check-circle me-2"></i>
                            Produit déjà lié à une plateforme Marjane Mall
                        </div>
                    ) : marjanePlateformIds.size === 0 ? (
                        <div className="alert alert-info">
                            Aucune plateforme Marjane Mall n&apos;est configurée.
                        </div>
                    ) : marjaneLeafOptions.length === 0 ? (
                        <div className="alert alert-info">
                            Aucune catégorie Marjane Mall (`type=marjanemall`) n&apos;est disponible.
                        </div>
                    ) : (
                        <div>
                            <div className="mb-3">
                                <label className="form-label">
                                    Rechercher une catégorie Marjane Mall (enfant)
                                </label>
                                <Autocomplete
                                    options={marjaneLeafOptions}
                                    value={selectedMarjaneSearchOption}
                                    onChange={(_, newValue) => {
                                        if (newValue?.node) {
                                            selectMarjaneLeafCategory(newValue.node);
                                        } else {
                                            setSelectedMarjaneLeafCategory(null);
                                            setLocalMarjaneCategoryId('');
                                        }
                                    }}
                                    getOptionLabel={(option) => option?.label || ''}
                                    renderOption={renderOptionWithKey}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    renderInput={(params) => (
                                        <TextField {...params} placeholder="Tapez pour rechercher (path, nom, remote_id)" />
                                    )}
                                />
                            </div>

                            <div className="mb-3">
                                <label className="form-label">
                                    Arbre des catégories Marjane Mall
                                </label>
                                <Box
                                    sx={{
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 1,
                                        maxHeight: 320,
                                        overflowY: 'auto',
                                        bgcolor: '#fafafa'
                                    }}
                                >
                                    <List dense>
                                        {renderMarjaneCategoryTree(marjaneCategoryTree)}
                                    </List>
                                </Box>
                            </div>

                            {selectedMarjaneLeafCategory && (
                                <div className="alert alert-secondary py-2">
                                    <strong>Catégorie sélectionnée:</strong> {selectedMarjaneLeafCategory.name}
                                    <br />
                                    <small>remote_id: {selectedMarjaneLeafCategory.remoteId || 'non défini'}</small>
                                </div>
                            )}

                            <button
                                className="btn btn-primary w-100"
                                onClick={handleMarjaneSubmit}
                                disabled={isMarjaneSubmitting || !selectedMarjaneLeafCategory || !selectedMarjaneLeafCategory?.remoteId}
                            >
                                {isMarjaneSubmitting ? 'Publication...' : 'Ajouter à Marjane Mall'}
                            </button>
                        </div>
                    )}
                </TabPanel>

                {/* Instagram Tab */}
                <TabPanel value={activeTab} index={6}>
                    {hasInstagram ? (
                        <div className="alert alert-success">
                            <i className="fa-solid fa-check-circle me-2"></i>
                            Produit déjà publié sur Instagram
                        </div>
                    ) : (
                        <div>
                            <div className="mb-3">
                                <label className="form-label h5">
                                    <i className="fa-brands fa-instagram me-2"></i>
                                    Caption Instagram
                                </label>
                                <textarea 
                                    style={{ width: '100%' }} 
                                    className='form-control'
                                    value={instagramDescriptionDraft} 
                                    rows={15}
                                    onChange={handleInstagramDescriptionChange}
                                    onFocus={(e) => {
                                        console.log('[Instagram textarea] onFocus', {
                                            value: e.target.value,
                                            activeElement: document.activeElement
                                        });
                                    }}
                                    onBlur={(e) => {
                                        console.log('[Instagram textarea] onBlur', {
                                            value: e.target.value,
                                            relatedTarget: e.relatedTarget
                                        });
                                    }}
                                    onClick={(e) => {
                                        console.log('[Instagram textarea] onClick', {
                                            target: e.target,
                                            currentTarget: e.currentTarget
                                        });
                                    }}
                                    onMouseDown={(e) => {
                                        console.log('[Instagram textarea] onMouseDown', {
                                            button: e.button,
                                            target: e.target
                                        });
                                    }}
                                    disabled={isInstagramSubmitting}
                                    autoComplete="off"
                                />
                            </div>
                            <button
                                className="btn btn-primary w-100"
                                onClick={handleInstagramSubmit}
                                disabled={isInstagramSubmitting}
                            >
                                {isInstagramSubmitting ? 'Publication...' : 'Poster sur Instagram'}
                            </button>
                        </div>
                    )}
                </TabPanel>

                {/* Compta Tab */}
                <TabPanel value={activeTab} index={7}>
                    {isSelectedDolibarrLinked ? (
                        <div>
                            <div className="alert alert-success">
                                <i className="fa-solid fa-check-circle me-2"></i>
                                Produit déjà créé en compta
                                {selectedDolibarrLinkedEntry?.dolibarrRef ? (
                                    <>
                                        <br />
                                        <small>Référence: <code>{selectedDolibarrLinkedEntry.dolibarrRef}</code></small>
                                    </>
                                ) : null}
                                {selectedDolibarrLinkedEntry?.companyName ? (
                                    <>
                                        <br />
                                        <small>Société: <code>{selectedDolibarrLinkedEntry.companyName}</code></small>
                                    </>
                                ) : null}
                            </div>
                            <div className="row g-3 mb-3">
                                <div className="col-md-8">
                                    <label className="form-label">Société compta</label>
                                    <Autocomplete
                                        disablePortal
                                        options={dolibarrCompanyOptions}
                                        value={selectedDolibarrCompanyOption}
                                        getOptionLabel={(opt) => opt?.label || ''}
                                        renderOption={renderOptionWithKey}
                                        isOptionEqualToValue={(opt, val) => normalizeId(opt?.value) === normalizeId(val?.value)}
                                        onChange={(_, newValue) => {
                                            setLocalDolibarrCompanyId(newValue?.value || '');
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params} placeholder="Sélectionner une société compta" size="small" />
                                        )}
                                        disabled={isDolibarrSubmitting || isDolibarrStockLoading || isDolibarrStockSubmitting}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Stock compta</label>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        value={dolibarrTargetStock}
                                        onChange={(e) => setDolibarrTargetStock(e.target.value)}
                                        placeholder={isDolibarrStockLoading ? 'Chargement...' : 'Stock'}
                                        inputProps={{ step: '1' }}
                                        disabled={isDolibarrStockLoading || isDolibarrStockSubmitting || !dolibarrWarehouseId}
                                    />
                                </div>
                            </div>
                            <div className="row g-3 mb-3">
                                <div className="col-md-4">
                                    <label className="form-label">Préfixe REF</label>
                                    <TextField
                                        fullWidth
                                        value={dolibarrRefPrefix}
                                        onChange={(e) => setDolibarrRefPrefix(e.target.value)}
                                        placeholder="Ex: OFL"
                                        disabled={isDolibarrSubmitting}
                                    />
                                </div>
                                <div className="col-md-8">
                                    <label className="form-label">REF Dolibarr</label>
                                    <TextField
                                        fullWidth
                                        value={dolibarrFinalRef}
                                        onChange={(e) => setDolibarrRefBase(sanitizeDolibarrRefPart(e.target.value))}
                                        helperText="Base unique generée automatiquement. Ajoutez un préfixe si vous voulez."
                                        disabled={isDolibarrSubmitting}
                                    />
                                </div>
                            </div>
                            <div className="card mb-3">
                                <div className="card-body">
                                    <div><strong>Prix local:</strong> {product?.regular_price ?? '—'}</div>
                                    <div><strong>Code-barres:</strong> {product?.barcode || '—'}</div>
                                    <div><strong>Produit compta:</strong> {selectedDolibarrLinkedEntry?.dolibarrId || '—'} / {selectedDolibarrLinkedEntry?.dolibarrRef || '—'}</div>
                                    {isDolibarrStockLoading ? (
                                        <div className="mt-2 text-muted">Chargement du stock compta...</div>
                                    ) : dolibarrWarehouseOptions.length === 0 ? (
                                        <div className="alert alert-warning mt-3 mb-0">
                                            Aucun depot compta disponible pour ce produit.
                                        </div>
                                    ) : (
                                        <>
                                            <div className="mb-3 mt-3">
                                                <label className="form-label">Dépôt</label>
                                                <Autocomplete
                                                    disablePortal
                                                    options={dolibarrWarehouseOptions}
                                                    value={dolibarrWarehouseOptions.find((option) => option.value === dolibarrWarehouseId) || null}
                                                    getOptionLabel={(opt) => opt?.label || ''}
                                                    renderOption={renderOptionWithKey}
                                                    isOptionEqualToValue={(opt, val) => normalizeId(opt?.value) === normalizeId(val?.value)}
                                                    onChange={(_, newValue) => {
                                                        const nextWarehouseId = newValue?.value || '';
                                                        setDolibarrWarehouseId(nextWarehouseId);
                                                        const matchingEntry = (Array.isArray(dolibarrStockState?.stock) ? dolibarrStockState.stock : []).find((entry) => `${entry?.warehouse_id || entry?.id || entry?.fk_entrepot || ''}` === `${nextWarehouseId}`);
                                                        const nextStock = Number(matchingEntry?.stock_reel ?? matchingEntry?.real ?? matchingEntry?.qty ?? matchingEntry?.stock ?? 0);
                                                        if (Number.isFinite(nextStock)) {
                                                            setDolibarrTargetStock(String(nextStock));
                                                        }
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField {...params} placeholder="Sélectionner un dépôt" size="small" />
                                                    )}
                                                    disabled={isDolibarrStockSubmitting || dolibarrWarehouseOptions.length === 0}
                                                />
                                            </div>
                                            <div className="card bg-light border-0 mb-3">
                                                <div className="card-body py-2">
                                                    <div><strong>Stock disponible:</strong> {dolibarrCurrentStockValue}</div>
                                                    <div className="small text-muted">Le champ stock en haut est pré-rempli avec le stock actuel. Modifiez-le puis enregistrez.</div>
                                                </div>
                                            </div>
                                            <div className="small text-muted mb-3">
                                                Stock actuel: {dolibarrSelectedStockEntry ? (dolibarrSelectedStockEntry?.stock_reel ?? dolibarrSelectedStockEntry?.real ?? dolibarrSelectedStockEntry?.qty ?? dolibarrSelectedStockEntry?.stock ?? 0) : '—'}
                                            </div>
                                            <button
                                                className="btn btn-outline-primary w-100"
                                                onClick={handleDolibarrStockSubmit}
                                                disabled={isDolibarrStockSubmitting || !localDolibarrCompanyId || !dolibarrWarehouseId || dolibarrTargetStock === ''}
                                            >
                                                {isDolibarrStockSubmitting ? 'Mise à jour...' : 'Mettre à jour le stock compta'}
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <div className="alert alert-info">
                                <i className="fa-solid fa-circle-info me-2"></i>
                                Cette action créera un produit compta avec le nom, le code-barres, la référence, la description, le prix choisi et l’image principale comme document.
                            </div>
                            <div className="row g-3 mb-3">
                                <div className="col-md-5">
                                    {dolibarrCompanyOptions.length === 0 ? (
                                        <div className="alert alert-warning mb-0">
                                            Aucune configuration société n&apos;est disponible sur la plateforme compta.
                                        </div>
                                    ) : (
                                        <>
                                            <label className="form-label">Société compta</label>
                                            <Autocomplete
                                                disablePortal
                                                options={dolibarrCompanyOptions}
                                                value={selectedDolibarrCompanyOption}
                                                getOptionLabel={(opt) => opt?.label || ''}
                                                renderOption={renderOptionWithKey}
                                                isOptionEqualToValue={(opt, val) => normalizeId(opt?.value) === normalizeId(val?.value)}
                                                onChange={(_, newValue) => {
                                                    setLocalDolibarrCompanyId(newValue?.value || '');
                                                }}
                                                renderInput={(params) => (
                                                    <TextField {...params} placeholder="Sélectionner une société compta" size="small" />
                                                )}
                                                disabled={isDolibarrSubmitting}
                                            />
                                        </>
                                    )}
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">Stock compta</label>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        value={dolibarrTargetStock}
                                        onChange={(e) => setDolibarrTargetStock(e.target.value)}
                                        placeholder="Stock"
                                        inputProps={{ step: '1' }}
                                        disabled={isDolibarrSubmitting}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Prix compta</label>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        value={dolibarrPriceDraft}
                                        onChange={(e) => setDolibarrPriceDraft(e.target.value)}
                                        inputProps={{ min: 0, step: '0.01' }}
                                        disabled={isDolibarrSubmitting}
                                    />
                                </div>
                            </div>
                            <div className="card mb-3">
                                <div className="card-body">
                                    <div><strong>Nom:</strong> {product?.name || '—'}</div>
                                    <div><strong>Code-barres:</strong> {product?.barcode || '—'}</div>
                                    <div><strong>Référence source:</strong> {product?.reference || product?.ref || product?.barcode || product?._id || '—'}</div>
                                    <div><strong>Prix local:</strong> {product?.regular_price ?? '—'}</div>
                                    <div><strong>Prix compta:</strong> {dolibarrPriceDraft || '—'}</div>
                                    <div><strong>Image:</strong> {(product?.url_product || (Array.isArray(product?.gallery_images) && product.gallery_images[0])) ? 'Oui' : 'Non'}</div>
                                </div>
                            </div>
                            <button
                                className="btn btn-primary w-100"
                                onClick={handleDolibarrSubmit}
                                disabled={isDolibarrSubmitting || dolibarrCompanyOptions.length === 0 || !localDolibarrCompanyId}
                            >
                                {isDolibarrSubmitting ? 'Publication...' : 'Créer le produit en compta'}
                            </button>
                        </div>
                    )}
                </TabPanel>

                {/* Reset Tab */}
                <TabPanel value={activeTab} index={8}>
                    {resetPlateformRows.length === 0 ? (
                        <div className="alert alert-info mb-0">
                            Aucune plateforme liée à ce produit.
                        </div>
                    ) : (
                        <div className="list-group">
                            {resetPlateformRows.map((row) => {
                                const isDeleting = deletingPlateformIds.has(row.plateformId);
                                return (
                                    <div
                                        key={row.key}
                                        className="list-group-item d-flex justify-content-between align-items-center"
                                    >
                                        <div>
                                            <div style={{ fontWeight: 500 }}>{row.plateformName}</div>
                                            <small className="text-muted">{row.plateformId}</small>
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-outline-danger btn-sm"
                                            onClick={() => handleResetPlateformDelete(row.plateformId)}
                                            disabled={isDeleting}
                                            title="Supprimer le lien plateforme"
                                        >
                                            {isDeleting ? (
                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                            ) : (
                                                <i className="fa-solid fa-trash"></i>
                                            )}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </TabPanel>

                <div className="mt-3 d-flex justify-content-end">
                    <button className="btn btn-secondary" onClick={onClose}>
                        Fermer
                    </button>
                </div>
            </DialogContent>
        </Dialog>
        </>
    );
};

export default PublierPlateformesModal;
